import { z } from 'zod';

import { colorChoiceSchema, optionalPxSchema } from '@/features/storefront/colorChoice';
import {
  ANCHOR_SECTION_TYPES,
  SECTION_DEVICES,
  elementBase,
  isAnchorType,
  newElementId,
  optionalText,
  safeHref,
} from '@/features/storefront/sections/primitives';
import type {
  AnchorSectionType,
  SectionDevice,
} from '@/features/storefront/sections/primitives';
import {
  MAX_PARTS_PER_ANCHOR,
  pagePartSchema,
} from '@/features/storefront/sections/parts';
import {
  DEFAULT_SOCIAL_PLATFORMS,
  SOCIAL_ALIGNMENTS,
  SOCIAL_ICON_SIZES,
  SOCIAL_ICON_STYLES,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORM_LABELS,
} from '@/features/storefront/sections/social';
import {
  VIDEO_ASPECT_RATIOS,
  VIDEO_OVERLAY_POSITIONS,
  VIDEO_SOURCES,
} from '@/features/storefront/sections/video';

/**
 * The home page as data.
 *
 * Mirrors `app/schemas/store.py` - the same closed set of section types, the
 * same block shapes, the same limits. The server is what validates a save, so
 * these schemas exist for two other reasons:
 *
 * - the renderer gets sections it can trust rather than `Record<string, unknown>`;
 * - a section this build does not understand is *dropped*, not rendered as a
 *   hole. A theme saved by a newer deploy (or an older one, missing fields the
 *   renderer now expects) has to degrade to a shorter page, never a broken one.
 *
 * Editing lives in `features/store` and rendering in `features/storefront`, but
 * both read this file: two definitions of a section would drift, and the way
 * that drift shows up is a preview that lies about the live page.
 */

const SECTION_PADDINGS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
const SECTION_BACKGROUNDS = ['default', 'surface', 'primary'] as const;

/**
 * How much of the page column one section takes.
 *
 * A page is a stack of full-width bands until a merchant says otherwise, and
 * `full` - the default, and what every document saved before this field
 * existed parses to - is that stack. Anything narrower opts the section into a
 * row: consecutive narrow sections stand beside each other until the row is
 * full, and a narrow section with nothing to pair with is centred at the width
 * it asked for. That is both halves of what a merchant means by "put something
 * next to my contact form" and "make my contact form smaller" - one control,
 * because to the page they are the same thing.
 *
 * Twelfths rather than free percentages: a column system a merchant can only
 * land on sensible fractions of is one they cannot leave a two-pixel gutter in.
 */
const SECTION_SPANS = ['full', 'two_thirds', 'half', 'third'] as const;

/** What each span is worth, out of the twelve a row holds. */
const SPAN_UNITS: Record<SectionSpan, number> = {
  full: 12,
  two_thirds: 8,
  half: 6,
  third: 4,
};

const ROW_UNITS = 12;

/**
 * How tall a banner stands, and how wide it runs.
 *
 * `full` height is the viewport less the header, so a shop can open on a photo
 * that fills the screen. `full` width is the default for banners carrying an
 * image: a photograph stopped short of the window edges reads as a mistake,
 * and it is the look every shop platform ships. `contained` keeps the banner
 * inside the page column, framed like a card.
 */
const BANNER_HEIGHTS = ['sm', 'md', 'lg', 'full'] as const;
const BANNER_WIDTHS = ['full', 'contained'] as const;
/** How big a split hero's overlapping product stands, mirroring the server. */
export const HERO_PRODUCT_SIZES = ['sm', 'md', 'lg'] as const;

/**
 * The three widths a merchant designs for, and the other three things a
 * theme document is built from, are shared with the part schema - see
 * `primitives.ts` for why they live there. Re-exported here because this is
 * where the rest of the app has always imported them from, and a module that
 * moved should not be a hundred import statements to update.
 */
export { ANCHOR_SECTION_TYPES, SECTION_DEVICES, isAnchorType, newElementId, safeHref };
export type { AnchorSectionType, SectionDevice };

export const FEATURE_ICONS = [
  'truck',
  'shield',
  'return',
  'support',
  'payment',
  'sparkles',
  'leaf',
  'award',
  'gift',
  'clock',
  'heart',
  'package',
] as const;

export type SectionPadding = (typeof SECTION_PADDINGS)[number];
export type SectionBackground = (typeof SECTION_BACKGROUNDS)[number];
export type SectionSpan = (typeof SECTION_SPANS)[number];
export type FeatureIcon = (typeof FEATURE_ICONS)[number];

/** How many benefit badges a hero holds, and points an image-with-text band. Mirror `store.py`. */
export const MAX_HERO_BADGES = 4;
export const MAX_IMAGE_POINTS = 6;
export type BannerHeight = (typeof BANNER_HEIGHTS)[number];
export type BannerWidth = (typeof BANNER_WIDTHS)[number];

/** The narrowest a section's content may be set, in percent of the page. Mirrors `SectionBase` on the server. */
export const CONTENT_WIDTH_MIN = 30;
export const CONTENT_WIDTH_MOBILE_MIN = 50;

/**
 * Where a section can have come from other than the merchant's own hand. See
 * `origin` below; mirrors the section base in `app/schemas/store.py`.
 */
export const SECTION_ORIGINS = ['theme_sample'] as const;
export type SectionOrigin = (typeof SECTION_ORIGINS)[number];

/** The stamp on every section copied from a theme's sample shop. */
export const THEME_SAMPLE_ORIGIN: SectionOrigin = 'theme_sample';

/** How a section's top or bottom edge is drawn. Mirrors `SectionEdge` on the server. */
export const SECTION_EDGES = ['straight', 'wave', 'curve'] as const;
export type SectionEdgeShape = (typeof SECTION_EDGES)[number];

const sectionBase = {
  ...elementBase,
  visible: z.boolean().default(true),
  padding: z.enum(SECTION_PADDINGS).default('md'),
  /**
   * What the padding becomes on a narrower screen, or `null` for "the same".
   *
   * Inheritance is the default and stays the default: a merchant who never
   * opens the responsive group has authored one page, not three, and every
   * document saved before these fields existed parses to exactly that. Only
   * the devices they disagreed about carry a value.
   */
  padding_tablet: z.enum(SECTION_PADDINGS).nullish().transform((value) => value ?? null),
  padding_mobile: z.enum(SECTION_PADDINGS).nullish().transform((value) => value ?? null),
  /**
   * The screens this section is left off, for a merchant building a different
   * page for a phone. Empty - the default - is a section on every screen.
   */
  hidden_on: z.array(z.enum(SECTION_DEVICES)).default([]),
  background: z.enum(SECTION_BACKGROUNDS).default('default'),
  // Named `column_span` rather than `width`, which the hero and the slideshow
  // already spend on how far their artwork bleeds.
  column_span: z.enum(SECTION_SPANS).default('full'),
  /**
   * How wide the section's content runs, as a percentage of the page's width,
   * or `null` - "Fit" - for the width the section is designed at: prose at a
   * reading column, a grid at the whole page. The phone has its own, since a
   * column that suits a laptop is a sliver on a phone. See `contentWidthFor`.
   *
   * Out-of-range numbers are dropped to Fit rather than failing the section:
   * a width is a preference, and a page should never lose a section over one.
   */
  content_width: z
    .number()
    .nullish()
    .transform((value) => (value != null && value >= CONTENT_WIDTH_MIN && value <= 100 ? Math.round(value) : null)),
  content_width_mobile: z
    .number()
    .nullish()
    .transform((value) =>
      value != null && value >= CONTENT_WIDTH_MOBILE_MIN && value <= 100 ? Math.round(value) : null,
    ),
  /**
   * The band's own colours, over `background`. Optional - unset is the band as
   * `background` paints it - so every section saved before these existed, and
   * every section a theme's presets build, renders unchanged. See
   * `sectionAppearanceStyle`.
   */
  background_color: colorChoiceSchema,
  text_color: colorChoiceSchema,
  /** A line under the band, in pixels, and its colour. */
  divider_px: optionalPxSchema,
  divider_color: colorChoiceSchema,
  /** Exact padding in pixels, over the `padding` presets on every screen. */
  padding_top_px: optionalPxSchema,
  padding_bottom_px: optionalPxSchema,
  /**
   * The shape of the band's top and bottom edges: a wave or a curve drawn in
   * the band's own background (see `SectionEdge` in `SectionRenderer.tsx`).
   * Straight - the default, and what every section saved before these existed
   * parses to - is the plain edge; an unknown shape reads as straight.
   */
  edge_top: z.enum(SECTION_EDGES).catch('straight').default('straight'),
  edge_bottom: z.enum(SECTION_EDGES).catch('straight').default('straight'),
  /**
   * Where the section came from, when that was not the merchant: a section
   * copied from a theme's sample shop is stamped `theme_sample` (see
   * `merchantThemeDocument`), which is what lets one-click setup replace the
   * theme's demo words and pictures without touching anything the merchant
   * wrote. The server clears it on a section whose content they change.
   *
   * Never drawn and never edited, only carried: declared here so a save from
   * the customiser does not strip it. Absent stays absent; a value this build
   * does not know is read as no origin at all.
   */
  origin: z.enum(SECTION_ORIGINS).nullable().optional().catch(null),
};

/**
 * Which part of a picture stays in view when its frame is a different shape.
 *
 * A banner is as wide as the shopper's screen and as tall as a share of it,
 * so the same photo is cut differently on a laptop, a monitor and a phone -
 * and until this, always around its middle, which is where a portrait's
 * subject is least likely to have their head. `x` and `y` are percentages
 * across and down the picture (the point `object-position` keeps in place),
 * and `zoom` enlarges around that point. One point serves every screen, which
 * is why it is a point and not a crop: a crop is right for one shape only.
 *
 * `null` - every picture saved before this existed - is the middle, so nothing
 * already published moves.
 */
export const focalPointSchema = z.object({
  x: z.number().min(0).max(100).default(50),
  y: z.number().min(0).max(100).default(50),
  zoom: z.number().min(1).max(3).default(1),
  // A banner's own zoom on a phone, where it is nearly square rather than a
  // wide strip. Absent - every point saved before this - means `zoom` again.
  zoom_phone: z.number().min(1).max(3).nullish(),
});

const imageFocal = focalPointSchema.nullish().transform((value) => value ?? null);

export type FocalPoint = z.infer<typeof focalPointSchema>;

// --- Blocks ---------------------------------------------------------------
export const slideBlockSchema = z.object({
  ...elementBase,
  image_url: optionalText,
  image_focal: imageFocal,
  heading: optionalText,
  subheading: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
});

/** One picture in a hero's rotation. */
export const heroImageSchema = z.object({
  ...elementBase,
  image_url: optionalText,
  image_focal: imageFocal,
});

export const featureBlockSchema = z.object({
  ...elementBase,
  icon: z.enum(FEATURE_ICONS).default('sparkles'),
  title: z.string(),
  body: optionalText,
  // The figure the `stats` style draws large above the title - "15+", "10k".
  // Words rather than a number, because a statistic is read as written.
  // Ignored by the `icons` style; empty on every block saved before it.
  stat: optionalText,
});

/**
 * One short point with an icon in an image-with-text band's list - "Certified
 * doctors" beside a clinic's photo. Mirrors `ImagePointBlock` on the server.
 * The icons are the features strip's own; `shield` (a shield with a check in
 * it) is the default because the set has no plain tick. An icon this build
 * does not know reads as that default rather than failing the whole band.
 */
export const imagePointBlockSchema = z.object({
  ...elementBase,
  icon: z.enum(FEATURE_ICONS).catch('shield').default('shield'),
  text: z.string().default(''),
});

export const testimonialBlockSchema = z.object({
  ...elementBase,
  quote: z.string(),
  author: optionalText,
  role: optionalText,
  rating: z.number().nullish().transform((value) => value ?? null),
});

/**
 * One dated step of a shop's story. `date` is words ("2012", "Spring 2019",
 * "Today"), written the way the merchant would say it. Mirrors `TimelineBlock`.
 */
export const timelineBlockSchema = z.object({
  ...elementBase,
  date: z.string(),
  title: z.string(),
  body: optionalText,
  image_url: optionalText,
});

export const faqBlockSchema = z.object({
  ...elementBase,
  question: z.string(),
  answer: z.string(),
});

export const galleryBlockSchema = z.object({
  ...elementBase,
  image_url: z.string(),
  image_focal: imageFocal,
  caption: optionalText,
  href: optionalText,
});

/**
 * Where a product tab's products come from, mirroring `ProductTabSource` on the
 * server. Each is one storefront listing request - see `productTabs.ts`.
 */
export const PRODUCT_TAB_SOURCES = ['newest', 'best_selling', 'on_sale', 'category'] as const;

export type ProductTabSource = (typeof PRODUCT_TAB_SOURCES)[number];

/** One tab over a product row: its label, and which products it shows. */
export const productTabBlockSchema = z.object({
  ...elementBase,
  label: z.string().default('New arrivals'),
  source: z.enum(PRODUCT_TAB_SOURCES).default('newest'),
  // Read only by a `category` tab; empty until the merchant picks a shelf.
  category_id: z.string().nullish().transform((value) => value ?? null),
  // The round picture the `circles` tab style draws. Empty falls back to the
  // category's own picture, then the tab's first product.
  image_url: optionalText,
  image_focal: imageFocal,
});

/**
 * How a product tabs strip looks, mirroring `ProductTabsSection.tab_style`:
 * `circles` draws each tab as a round picture with its label under it.
 */
export const PRODUCT_TAB_STYLES = ['underline', 'pills', 'circles'] as const;

/**
 * The arrangements a promo grid's tiles stand in, mirroring `PromoGridLayout`
 * on the server. The count in each name is how many tiles it shows; every one
 * stacks to a single column on a phone.
 */
export const PROMO_GRID_LAYOUTS = [
  '2-equal',
  '3-equal',
  '4-equal',
  '1-large-2-small',
  '1-large-4-small',
  '2-large-3-small',
  // Every tile (up to six) as a narrow, tall card in one line; phones and
  // tablets scroll it sideways rather than stacking it.
  'row',
] as const;
export const PROMO_GRID_GAPS = ['none', 'sm', 'md', 'lg'] as const;
export const PROMO_GRID_HEIGHTS = ['sm', 'md', 'lg'] as const;
export const PROMO_TEXT_POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'center-left',
  'center',
  'center-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const;

/** How many tiles each layout shows. */
export const PROMO_LAYOUT_TILES: Record<PromoGridLayout, number> = {
  '2-equal': 2,
  '3-equal': 3,
  '4-equal': 4,
  '1-large-2-small': 3,
  '1-large-4-small': 5,
  '2-large-3-small': 5,
  row: 6,
};

export type PromoGridLayout = (typeof PROMO_GRID_LAYOUTS)[number];
export type PromoTextPosition = (typeof PROMO_TEXT_POSITIONS)[number];

/** One offer tile: a picture, or a palette colour when it has none, with words over it. */
export const promoTileBlockSchema = z.object({
  ...elementBase,
  image_url: optionalText,
  image_focal: imageFocal,
  eyebrow: optionalText,
  heading: optionalText,
  subheading: optionalText,
  cta_label: optionalText,
  href: optionalText,
  text_position: z.enum(PROMO_TEXT_POSITIONS).default('bottom-left'),
  // Unset picks for itself: white over a picture, the theme's text on a plain tile.
  text_color: colorChoiceSchema,
  overlay_opacity: z.number().default(30),
  // Unset takes the next palette colour in turn, so a fresh grid is coloured.
  background_color: colorChoiceSchema,
});

export const BRAND_STRIP_STYLES = ['row', 'carousel', 'grid'] as const;
export const BRAND_LOGO_HEIGHTS = ['sm', 'md', 'lg'] as const;

/** One brand the shop stocks: its logo, its name, and where it leads. */
export const brandBlockSchema = z.object({
  ...elementBase,
  image_url: optionalText,
  name: optionalText,
  href: optionalText,
});

export const LOOKBOOK_LAYOUTS = ['image_only', 'image_with_list'] as const;
export const LOOKBOOK_HOTSPOT_STYLES = ['dot', 'plus', 'number'] as const;
export const BEFORE_AFTER_ORIENTATIONS = ['horizontal', 'vertical'] as const;
/** One comparison, or two side by side. Mirrors `BeforeAfterLayout`. */
export const BEFORE_AFTER_LAYOUTS = ['single', 'pair', 'cards'] as const;
/** `panel` sets the comparisons in a rounded card inside the band. Mirrors `BeforeAfterFrame`. */
export const BEFORE_AFTER_FRAMES = ['none', 'panel'] as const;
/** Where an email signup's photos stand; `none` is the form on its own. */
export const NEWSLETTER_IMAGE_SIDES = ['none', 'left', 'right'] as const;
export const PRICING_PLANS_LAYOUTS = ['cards', 'intro_left'] as const;
export const PRICING_CARD_STYLES = ['outlined', 'filled', 'shadow', 'tinted'] as const;
export const PRODUCT_SPOTLIGHT_IMAGE_SIDES = ['left', 'right'] as const;
export const PRODUCT_SHOWCASE_IMAGE_SIDES = ['left', 'right'] as const;
/** Which corner of the scene the small product card overlaps. */
export const PRODUCT_SHOWCASE_CARD_POSITIONS = ['top', 'bottom'] as const;
/** The category buttons over a showcase. Mirrors `ProductShowcasePillStyle`. */
export const PRODUCT_SHOWCASE_PILL_STYLES = ['outline', 'filled'] as const;
/** The most categories a merchant may put on a showcase's buttons. */
export const PRODUCT_SHOWCASE_MAX_PILLS = 8;
/** How a gallery's pictures stand: a grid, or one row of squares. Mirrors `GalleryStyle`. */
export const GALLERY_STYLES = ['grid', 'strip'] as const;
/** How a features section draws its blocks. Mirrors `FeaturesSection.style`. */
export const FEATURE_STYLES = ['icons', 'stats'] as const;
export type FeatureStyle = (typeof FEATURE_STYLES)[number];

/** How a timeline runs: down the page, or across it. Mirrors `TimelineLayout`. */
export const TIMELINE_LAYOUTS = ['vertical', 'horizontal'] as const;
export type TimelineLayout = (typeof TIMELINE_LAYOUTS)[number];

/** Where an FAQ's photo stands; `none` is the plain list. Mirrors `FaqImageSide`. */
export const FAQ_IMAGE_SIDES = ['none', 'left', 'right'] as const;
/** A photo column beside the questions, or the photo behind the whole band. */
export const FAQ_IMAGE_STYLES = ['beside', 'background'] as const;

/**
 * One plan: a name, the merchant's own price, what it includes - one point per
 * line - and a button. Mirrors `PricingPlanBlock` in `app/schemas/store.py`.
 */
export const pricingPlanBlockSchema = z.object({
  ...elementBase,
  name: z.string().default(''),
  price: optionalText,
  period: optionalText,
  description: optionalText,
  features: optionalText,
  cta_label: optionalText,
  href: optionalText,
  badge: optionalText,
  featured: z.boolean().default(false),
  // A small line under the button - "Chat support included".
  note: optionalText,
  // The booking service the button books, by name; blank for a plain link.
  service: optionalText,
  // The product the button opens, by slug, for a plan sold as one; blank for a plain link.
  product: optionalText,
  /**
   * The session package the button sells (`/booking-packages`), hydrated by
   * the server from the plan; absent or null for every other plan. A card
   * with one buys it - sign-in, then the gateway - instead of following a link.
   */
  package_id: z.string().nullish(),
});

/**
 * One product tagged on a lookbook picture, at a point given in percent across
 * and down. `product` is the product's slug as the picker writes it; empty
 * until one is picked, and such a hotspot is not drawn on the shop.
 */
export const lookbookHotspotBlockSchema = z.object({
  ...elementBase,
  x: z.number().min(0).max(100).default(50),
  y: z.number().min(0).max(100).default(50),
  product: optionalText,
  label: optionalText,
});

/**
 * One profile a shop links out to.
 *
 * `platform` is what picks the mark, so the merchant never chooses an icon -
 * they choose a network and get its logo. That is also why the field is an
 * enum rather than the icon name a features block carries: a row of official
 * marks is only worth having if it cannot be set to the wrong one.
 *
 * `label` is empty for every named network and is what makes `custom` usable.
 * It overrides the platform's name where a merchant fills it in - a shop with
 * two Instagram accounts needs to tell them apart in the builder's list, and a
 * screen reader needs to announce something better than "Instagram" twice.
 *
 * `url` holds what the merchant typed, not what the storefront links to. See
 * `socialLinkHref`: a WhatsApp row accepts the shop's phone number and becomes
 * a `wa.me` link at render, and a field that rewrote itself while it was being
 * typed would be a field a merchant cannot correct.
 */
export const socialLinkBlockSchema = z.object({
  ...elementBase,
  platform: z.enum(SOCIAL_PLATFORMS).default('custom'),
  label: optionalText,
  url: optionalText,
});

/**
 * One thing an appointment business does, and how long it takes.
 *
 * `duration_minutes` is what makes this more than a price list: the slot grid
 * is built from it, so a forty-minute consultation and a ten-minute review
 * offered on the same opening hours produce two different sets of times.
 *
 * `price` is nullable and means it. Plenty of clinics and consultants quote on
 * consultation, and a field that forced a number would make them publish one
 * they do not stand behind.
 */
export const BOOKING_LOCATIONS = ['place', 'online', 'address'] as const;
export type BookingLocation = (typeof BOOKING_LOCATIONS)[number];

/** Rupees as a number or a decimal string, read as a number; nothing when it is not one. */
const hintAmount = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && !value.trim()) return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  });

/** See `bookingServiceSchema.payment`. Mirrors the hint `catalog.py` hydrates. */
export const bookingPaymentHintSchema = z.object({
  mode: z.string(),
  total: hintAmount,
  due_now: hintAmount,
});

export type BookingPaymentHint = z.infer<typeof bookingPaymentHintSchema>;

export const bookingServiceSchema = z.object({
  ...elementBase,
  name: z.string(),
  description: optionalText,
  duration_minutes: z.number().default(30),
  /**
   * A number, or the numeric string the server wrote before it serialised
   * prices as numbers (`"14999.00"`). A string used to fail this schema and
   * take the whole bookings section off the page with it; anything that is
   * not a number at all is read as "no price" rather than as a broken section.
   */
  price: z
    .union([z.number(), z.string()])
    .nullish()
    .transform((value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'string' && !value.trim()) return null;
      const amount = Number(value);
      return Number.isFinite(amount) ? amount : null;
    }),
  image_url: optionalText,
  image_focal: imageFocal,
  /**
   * Where the appointment happens: at the business (`place`, every service
   * written before this existed), `online`, or at the shopper's `address` -
   * which makes the booking form ask for one, and refuse without it.
   * Mirrors `BookingServiceBlock.location` on the server. Caught rather than
   * rejected, so a hand-edited value costs the service its place, not the page.
   */
  location: z.enum(BOOKING_LOCATIONS).catch('place').default('place'),
  /**
   * One line the shopper reads beside the service - "We'll email a meeting
   * link", "Parking at the back". No video integration sits behind `online`;
   * this is where the merchant says how it works.
   */
  location_note: optionalText,
  /**
   * What booking it costs now, on a shop that takes booking payments - the
   * mode (`FULL`, `DEPOSIT`, `PAY_AT_VENUE`, `CUSTOMER_CHOICE`), the price and
   * what is due up front. Hydrated by the server; absent or null when payments
   * are off or the service has no price, which is the booking it always was.
   * Only a hint for the summary: the server works the amount out again.
   */
  payment: bookingPaymentHintSchema.nullish().catch(undefined),
});

/**
 * When the business is open on one weekday.
 *
 * `weekday` is 0 for Monday, matching the server and `BOOKING_WEEKDAYS` below.
 * Times are wall-clock strings in the *store's* timezone - "we open at nine" is
 * a fact about a weekday, and the day it lands on is what makes it an instant.
 * The server does that conversion; nothing here ever turns one into a `Date`.
 */
export const bookingHoursSchema = z.object({
  weekday: z.number(),
  closed: z.boolean().default(false),
  opens: z.string().default('09:00'),
  closes: z.string().default('17:00'),
});

/** The week, as the inspector lists it. Monday first, matching `weekday`. */
export const BOOKING_WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/**
 * Monday to Saturday, nine to six - and Sunday shut.
 *
 * A default rather than an empty week, because a section offering no times at
 * all the moment it is added reads as broken. Mirrors `default_booking_hours`
 * on the server, which is what a document saved without hours validates to.
 */
export function defaultBookingHours(): BookingHours[] {
  return BOOKING_WEEKDAYS.map((_, weekday) =>
    bookingHoursSchema.parse({ weekday, opens: '09:00', closes: '18:00', closed: weekday === 6 }),
  );
}

export type SlideBlock = z.infer<typeof slideBlockSchema>;
export type HeroImage = z.infer<typeof heroImageSchema>;
export type FeatureBlock = z.infer<typeof featureBlockSchema>;
export type ImagePointBlock = z.infer<typeof imagePointBlockSchema>;
export type TestimonialBlock = z.infer<typeof testimonialBlockSchema>;
export type FaqBlock = z.infer<typeof faqBlockSchema>;
export type TimelineBlock = z.infer<typeof timelineBlockSchema>;
export type GalleryBlock = z.infer<typeof galleryBlockSchema>;
export type PromoTileBlock = z.infer<typeof promoTileBlockSchema>;
export type BrandBlock = z.infer<typeof brandBlockSchema>;
export type LookbookHotspotBlock = z.infer<typeof lookbookHotspotBlockSchema>;
export type BookingService = z.infer<typeof bookingServiceSchema>;
export type BookingHours = z.infer<typeof bookingHoursSchema>;

// --- Sections -------------------------------------------------------------
export const heroSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('hero'),
  heading: z.string(),
  subheading: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
  // A second, outlined button beside the first, drawn only with words.
  secondary_cta_label: optionalText,
  secondary_cta_href: optionalText,
  // Short benefit labels with a check under the buttons; at most four, as the
  // server holds them. Absent on every hero saved before them: none drawn.
  badges: z.array(z.string()).max(MAX_HERO_BADGES).catch([]).default([]),
  // The hero's first picture, and the only one a document saved before the
  // rotation existed carries. `heroImageRows` is what reads a hero's pictures;
  // nothing reaches for this field directly.
  image_url: optionalText,
  images: z.array(heroImageSchema).default([]),
  image_autoplay: z.boolean().default(true),
  image_interval_seconds: z.number().default(6),
  layout: z.enum(['center', 'split']).default('center'),
  height: z.enum(BANNER_HEIGHTS).default('md'),
  width: z.enum(BANNER_WIDTHS).default('full'),
  overlay_opacity: z.number().default(40),
  // A cut-out product standing over the seam of a `split` hero; ignored by
  // the centred layout. `product_overlap` is what the theme builder sets to
  // ask for one - only the picture is read here.
  product_image_url: optionalText,
  product_image_size: z.enum(HERO_PRODUCT_SIZES).catch('md').default('md'),
  product_overlap: z.boolean().default(false),
  // A short line over the heading. Mirrors `HeroSection.eyebrow`.
  eyebrow: optionalText,
  // The call to action as the theme's button, or an underlined text link.
  cta_style: z.enum(['button', 'link']).catch('button').default('button'),
  // A split hero's picture: framed in its column, or flush - filling its half
  // of a contained hero's card to the card's edges.
  image_fit: z.enum(['framed', 'flush']).catch('framed').default('framed'),
});

export const slideshowSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('slideshow'),
  height: z.enum(BANNER_HEIGHTS).default('md'),
  width: z.enum(BANNER_WIDTHS).default('full'),
  autoplay: z.boolean().default(true),
  interval_seconds: z.number().default(6),
  blocks: z.array(slideBlockSchema).default([]),
});

export const featuredProductsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('featured_products'),
  title: z.string().default('Featured products'),
  subtitle: optionalText,
  limit: z.number().default(8),
  columns: z.number().default(4),
  category_id: z.string().nullish().transform((value) => value ?? null),
  // `carousel`: one row slid through with arrows, `columns` in view at once.
  layout: z.enum(['grid', 'carousel']).default('grid'),
});

/**
 * The products this shopper opened lately, read from their own browser (see
 * `recentlyViewed.ts`). Mirrors `RecentlyViewedSection` on the server.
 */
export const recentlyViewedSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('recently_viewed'),
  title: z.string().default('Recently viewed'),
  limit: z.number().int().min(4).max(12).catch(4).default(4),
  layout: z.enum(['grid', 'carousel']).catch('grid').default('grid'),
});

/** "New arrivals | Best sellers | On sale" tabs over one product grid or carousel. */
export const productTabsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('product_tabs'),
  title: optionalText,
  subtitle: optionalText,
  layout: z.enum(['grid', 'carousel']).default('grid'),
  columns: z.number().default(4),
  limit: z.number().default(8),
  tab_style: z.enum(PRODUCT_TAB_STYLES).default('underline'),
  blocks: z.array(productTabBlockSchema).default([]),
  // An optional centred "view all" button under the products.
  cta_label: optionalText,
  cta_href: optionalText,
});

export const categoriesSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('categories'),
  title: z.string().default('Shop by category'),
  subtitle: optionalText,
  columns: z.number().default(3),
  // A grid, or one row slid through with a progress bar under it.
  layout: z.enum(['grid', 'carousel']).catch('grid').default('grid'),
  // `stand` stands each picture on a light panel it overhangs at the bottom.
  tile_style: z.enum(['photo', 'stand']).catch('photo').default('photo'),
  // Where the title and subtitle stand: the theme's own way, or set here.
  heading_align: z.enum(['theme', 'left', 'center']).catch('theme').default('theme'),
});

export const CATEGORY_STRIP_ICON_SHAPES = ['circle', 'square', 'none'] as const;
export const CATEGORY_STRIP_ICON_SIZES = ['sm', 'md'] as const;
export const CATEGORY_STRIP_LABEL_POSITIONS = ['below', 'beside'] as const;

/**
 * One thin row of small icon-led department links, usually just under the
 * header. The links are always the shop's own top-level categories - the
 * section stores no names or links of its own. Mirrors `CategoryStripSection`
 * in `backend/app/schemas/store.py`.
 */
export const categoryStripSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('category_strip'),
  title: optionalText,
  // Caught rather than rejected: a hand-edited 50 costs the count, not the section.
  max_items: z.number().int().min(4).max(20).catch(10).default(10),
  icon_shape: z.enum(CATEGORY_STRIP_ICON_SHAPES).catch('circle').default('circle'),
  icon_size: z.enum(CATEGORY_STRIP_ICON_SIZES).catch('md').default('md'),
  label_position: z.enum(CATEGORY_STRIP_LABEL_POSITIONS).catch('below').default('below'),
  item_dividers: z.boolean().default(false),
});

export const imageWithTextSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('image_with_text'),
  image_url: optionalText,
  image_focal: imageFocal,
  heading: z.string(),
  body: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
  image_side: z.enum(['left', 'right']).default('left'),
  // An icon list under the body; up to six, as the server holds them.
  points: z.array(imagePointBlockSchema).max(MAX_IMAGE_POINTS).catch([]).default([]),
  // Where those points stand: under the paragraph (every band saved before
  // this setting), or as a feature list in a column of their own from `lg` up
  // - photo | words | points - stacking on a phone. Nothing to draw without
  // points; an unknown word reads as "below", the look a band always had.
  points_layout: z.enum(['below', 'column']).catch('below').default('below'),
});

export const bannerSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('banner'),
  text: z.string(),
  href: optionalText,
  link_label: optionalText,
});

export const textSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('text'),
  title: optionalText,
  body: z.string(),
  align: z.enum(['left', 'center']).default('left'),
});

export const LINK_DIRECTORY_SOURCES = ['categories', 'manual'] as const;
export const LINK_DIRECTORY_SEPARATORS = ['pipe', 'comma', 'dot'] as const;
export const LINK_DIRECTORY_HEADING_STYLES = ['bold', 'muted'] as const;
export const LINK_DIRECTORY_TEXT_SIZES = ['xs', 'sm'] as const;

/** One small text link in a directory group: a label and a path on the shop. */
export const linkDirectoryLinkSchema = z.object({
  label: z.string().default(''),
  href: z.string().default(''),
});

/** A directory group: a short heading and its inline run of links. */
export const linkDirectoryBlockSchema = z.object({
  ...elementBase,
  heading: optionalText,
  links: z.array(linkDirectoryLinkSchema).default([]),
});

/**
 * A dense directory of grouped text links - "popular searches", SEO link
 * blocks. `categories` fills it from the shop's own category tree; `manual`
 * draws the merchant's groups. Mirrors `LinkDirectorySection` on the server.
 */
export const linkDirectorySectionSchema = z.object({
  ...sectionBase,
  type: z.literal('link_directory'),
  title: optionalText,
  source: z.enum(LINK_DIRECTORY_SOURCES).catch('categories').default('categories'),
  separator: z.enum(LINK_DIRECTORY_SEPARATORS).catch('pipe').default('pipe'),
  columns: z.number().int().min(1).max(3).catch(1).default(1),
  heading_style: z.enum(LINK_DIRECTORY_HEADING_STYLES).catch('bold').default('bold'),
  text_size: z.enum(LINK_DIRECTORY_TEXT_SIZES).catch('xs').default('xs'),
  blocks: z.array(linkDirectoryBlockSchema).default([]),
});

export const featuresSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('features'),
  title: optionalText,
  subtitle: optionalText,
  columns: z.number().default(4),
  blocks: z.array(featureBlockSchema).default([]),
  // `icons` draws an icon over each title; `stats` draws each block's `stat`
  // large with its title under it ("15+ years"). Caught, so an unknown style
  // costs the merchant the style rather than the whole section.
  style: z.enum(FEATURE_STYLES).catch('icons').default('icons'),
});

export const testimonialsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('testimonials'),
  title: z.string().default('What our customers say'),
  subtitle: optionalText,
  columns: z.number().default(3),
  blocks: z.array(testimonialBlockSchema).default([]),
});

export const faqSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('faq'),
  title: z.string().default('Frequently asked questions'),
  // The questions. On a store whose FAQs live in the `/faqs` catalog the
  // backend writes these from that catalog every time it serves the config,
  // so - like a bookings section's services - they are read-only in the
  // customizer, which picks which questions show (`faq_ids`).
  blocks: z.array(faqBlockSchema).default([]),
  /**
   * Which catalog questions this section shows, in catalog order. Empty is
   * every active question, so a question added on `/faqs` appears without a
   * trip to the customizer. Migrating a shop pins each section that already
   * had questions to its own, since themes put different questions on
   * different pages. Mirrors `FaqSection.faq_ids` on the server.
   */
  faq_ids: z.array(z.string()).default([]),
  // Ignored while `image_side` is `none`: the list then reads as it always did.
  image_url: optionalText,
  image_focal: imageFocal,
  image_side: z.enum(FAQ_IMAGE_SIDES).catch('none').default('none'),
  image_style: z.enum(FAQ_IMAGE_STYLES).catch('beside').default('beside'),
});

/**
 * A shop's story as dated milestones - the About page's "since 2012" line.
 * Mirrors `TimelineSection` on the server.
 */
export const timelineSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('timeline'),
  title: optionalText.default('Our story'),
  subtitle: optionalText,
  layout: z.enum(TIMELINE_LAYOUTS).catch('vertical').default('vertical'),
  blocks: z.array(timelineBlockSchema).default([]),
});

export const gallerySectionSchema = z.object({
  ...sectionBase,
  type: z.literal('gallery'),
  title: optionalText,
  columns: z.number().default(3),
  blocks: z.array(galleryBlockSchema).default([]),
  // `strip`: one row of square tiles - a social photo row - scrolling sideways on phones.
  style: z.enum(GALLERY_STYLES).catch('grid').default('grid'),
  // A strip running edge to edge of the window; ignored by the grid.
  full_bleed: z.boolean().default(false),
  // A button under the pictures, shown once it has both words and a link.
  cta_label: optionalText,
  href: optionalText,
});

/** A mosaic of two to six offer tiles - "Up to 50% off headphones". */
export const promoGridSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('promo_grid'),
  title: optionalText,
  subtitle: optionalText,
  layout: z.enum(PROMO_GRID_LAYOUTS).default('1-large-2-small'),
  gap: z.enum(PROMO_GRID_GAPS).default('md'),
  height: z.enum(PROMO_GRID_HEIGHTS).default('md'),
  blocks: z.array(promoTileBlockSchema).default([]),
});

/**
 * A row of brand logos. With no logos uploaded the storefront shows the
 * brands on the shop's own products as wordmarks instead, and leaves the
 * section off a page that has neither.
 */
export const brandStripSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('brand_strip'),
  title: optionalText,
  subtitle: optionalText,
  style: z.enum(BRAND_STRIP_STYLES).default('row'),
  grayscale: z.boolean().default(true),
  logo_height: z.enum(BRAND_LOGO_HEIGHTS).default('md'),
  blocks: z.array(brandBlockSchema).default([]),
});

/**
 * A shoppable picture: a styled room or outfit with up to eight products
 * tagged on it. Left off the shop until it has a picture.
 */
export const lookbookSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('lookbook'),
  title: optionalText,
  subtitle: optionalText,
  image_url: optionalText,
  image_focal: imageFocal,
  layout: z.enum(LOOKBOOK_LAYOUTS).default('image_only'),
  hotspot_style: z.enum(LOOKBOOK_HOTSPOT_STYLES).default('dot'),
  blocks: z.array(lookbookHotspotBlockSchema).default([]),
});

/**
 * Two pictures of the same thing under a draggable divider. Left off the shop
 * until it has both.
 */
export const beforeAfterSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('before_after'),
  title: optionalText,
  subtitle: optionalText,
  before_image_url: optionalText,
  before_image_focal: imageFocal,
  after_image_url: optionalText,
  after_image_focal: imageFocal,
  before_label: optionalText,
  after_label: optionalText,
  orientation: z.enum(BEFORE_AFTER_ORIENTATIONS).default('horizontal'),
  start_position: z.number().min(0).max(100).default(50),
  caption: optionalText,
  // `pair` draws a second comparison beside the first once both its photos are in.
  layout: z.enum(BEFORE_AFTER_LAYOUTS).default('single'),
  second_before_image_url: optionalText,
  second_before_image_focal: imageFocal,
  second_after_image_url: optionalText,
  second_after_image_focal: imageFocal,
  second_caption: optionalText,
  frame: z.enum(BEFORE_AFTER_FRAMES).catch('none').default('none'),
  // `cards` only: a small tag on each photo - "120 kg", "Week 1".
  before_tag: optionalText,
  after_tag: optionalText,
  second_before_tag: optionalText,
  second_after_tag: optionalText,
});

/**
 * Two to four plans side by side. Left off the shop until a plan has a name.
 * Mirrors `PricingPlansSection` in `app/schemas/store.py`.
 */
export const pricingPlansSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('pricing_plans'),
  title: optionalText,
  subtitle: optionalText,
  layout: z.enum(PRICING_PLANS_LAYOUTS).default('cards'),
  card_style: z.enum(PRICING_CARD_STYLES).catch('outlined').default('outlined'),
  // The plan cards. On a store whose plans live in the `/pricing-plans`
  // catalog the backend writes these from that catalog every time it serves
  // the config, so they are read-only here: the customizer picks which plans
  // show (`plan_ids`) and the panel page edits the plans themselves.
  blocks: z.array(pricingPlanBlockSchema).default([]),
  /**
   * Which catalog plans this section shows, in catalog order. Empty is every
   * active plan - the default, so a new plan appears without a second trip to
   * the customizer. Mirrors `PricingPlansSection.plan_ids` on the server.
   */
  plan_ids: z.array(z.string()).default([]),
  // The intro column's own words, for `intro_left`. All optional.
  eyebrow: optionalText,
  body: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
});

/**
 * One product, large, with its price and buttons beside a big picture.
 * `product` is the picked slug; empty shows the shop's newest product.
 */
export const productSpotlightSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('product_spotlight'),
  eyebrow: optionalText,
  product: optionalText,
  image_url: optionalText,
  image_focal: imageFocal,
  image_side: z.enum(PRODUCT_SPOTLIGHT_IMAGE_SIDES).default('right'),
  show_description: z.boolean().default(true),
});

/**
 * A dark band: a big product scene on one side, a heading, words and a button
 * on the other, and a small product card overlapping the scene. `product` is
 * the picked slug; empty shows the shop's newest product. Mirrors
 * `ProductShowcaseSection` in `app/schemas/store.py`.
 */
export const productShowcaseSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('product_showcase'),
  eyebrow: optionalText,
  heading: z.string().default('Made for your routine'),
  body: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
  image_url: optionalText,
  image_focal: imageFocal,
  image_side: z.enum(PRODUCT_SHOWCASE_IMAGE_SIDES).catch('left').default('left'),
  product: optionalText,
  card_position: z.enum(PRODUCT_SHOWCASE_CARD_POSITIONS).catch('bottom').default('bottom'),
  show_card: z.boolean().default(true),
  // A row of category buttons above the band. No picks: the first six top-level categories.
  show_category_pills: z.boolean().default(false),
  pill_category_ids: z
    .array(z.string())
    .catch([])
    .default([])
    .transform((ids) => ids.slice(0, PRODUCT_SHOWCASE_MAX_PILLS)),
  pill_style: z.enum(PRODUCT_SHOWCASE_PILL_STYLES).catch('outline').default('outline'),
  // `carousel`: the heading and pills over a panel of the scene and a paged
  // product card. Mirrors `ProductShowcaseSection.layout`.
  layout: z.enum(['words', 'carousel']).catch('words').default('words'),
});

/**
 * One video, from YouTube or from the merchant's own file.
 *
 * **Why two sources rather than one.** A shop's video is either something it
 * published to YouTube - where the hosting, the bandwidth and the transcoding
 * are somebody else's problem - or a short clip it owns and does not want on a
 * platform that will suggest a competitor when it ends. Both are ordinary, so
 * `source` says which player to draw rather than the renderer guessing from
 * whichever field happens to be filled in. That also makes switching
 * non-destructive: a merchant who tries a file and goes back to the embed
 * finds their link still there.
 *
 * **The link is stored, not the id.** `youtube_url` keeps what the merchant
 * pasted; the renderer pulls the id out of it. The field a merchant edits is
 * therefore the one they filled in, rather than an eleven-character code they
 * never typed.
 *
 * **Both sources are optional and mean it.** A section is added before it is
 * filled in, so an empty video section is a valid one - it renders as a prompt
 * in the customiser and as nothing at all on the shopper's page, exactly as a
 * gallery with no images does.
 *
 * Controls are not a setting. Play/pause, volume, progress and fullscreen are
 * always on, because a video a shopper cannot pause is a thing done to them.
 * `autoplay` is offered because a muted looping clip is a legitimate design,
 * and it forces `muted` at render: every browser blocks an unmuted autoplay.
 */
export const videoSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('video'),
  title: optionalText,
  subtitle: optionalText,
  source: z.enum(VIDEO_SOURCES).default('youtube'),
  youtube_url: optionalText,
  /** A URL from `POST /media/store-videos`, never a path a merchant types. */
  video_url: optionalText,
  aspect_ratio: z.enum(VIDEO_ASPECT_RATIOS).default('16:9'),
  autoplay: z.boolean().default(false),
  loop: z.boolean().default(false),
  muted: z.boolean().default(false),
  /**
   * Promotional words over the player's frame. They give way once a shopper
   * starts the video and only the button takes a tap, so the controls stay
   * the shopper's. `none` is the plain player.
   */
  overlay_text_position: z.enum(VIDEO_OVERLAY_POSITIONS).catch('none').default('none'),
  overlay_heading: optionalText,
  overlay_subheading: optionalText,
  overlay_cta_label: optionalText,
  /** Blank sends the button to the catalog. */
  overlay_cta_href: optionalText,
  /** Generously rounded corners, whatever the theme's own radius. */
  rounded: z.boolean().default(false),
});

/**
 * Where a shop's profiles are, as a row of the networks' own marks.
 *
 * **The seven are the section, not a menu behind it.** A new one arrives with
 * YouTube, Facebook, LinkedIn, X, Instagram, Pinterest and WhatsApp already
 * listed and every URL empty - see `DEFAULT_SOCIAL_PLATFORMS`. A merchant's
 * job is then to paste the addresses they have and ignore the rows they do
 * not, rather than to find each network in a dropdown first. Nothing is
 * claimed by doing this, because a row with no URL is not rendered: the
 * default is a set of prompts, which is exactly what the empty-testimonials
 * rule elsewhere in this file is protecting.
 *
 * **A link is shown only when it goes somewhere.** `socialLinkHref` is the one
 * judge of that, and the renderer, the inspector's validation and the server
 * all ask it - so a half-typed address is a missing icon rather than a dead
 * one, and there is no second definition of "configured" to drift.
 *
 * **Style is a property of the row, not of a link.** One choice of shape, size
 * and colour for all of them, because seven marks set at seven sizes is not a
 * thing any shop wants and offering it per-link would mean every merchant has
 * to make the same decision seven times.
 */
export const socialLinksSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('social_links'),
  title: optionalText,
  subtitle: optionalText,
  icon_style: z.enum(SOCIAL_ICON_STYLES).default('outlined'),
  icon_size: z.enum(SOCIAL_ICON_SIZES).default('md'),
  align: z.enum(SOCIAL_ALIGNMENTS).default('center'),
  /**
   * Each network in its own colour, rather than in the theme's.
   *
   * Off by default. A shop's own page is the one place its palette should win,
   * and seven brand colours in a line is the loudest thing on most storefronts
   * - but it is also what a lot of merchants picture when they ask for "the
   * real icons", so it is one switch away rather than absent.
   */
  brand_colors: z.boolean().default(false),
  blocks: z.array(socialLinkBlockSchema).default([]),
});

export const contactFormSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('contact_form'),
  title: optionalText,
  subtitle: optionalText,
  show_phone: z.boolean().default(true),
  show_comment: z.boolean().default(true),
  button_label: z.string().default('Submit'),
  success_message: z
    .string()
    .default("Thanks for getting in touch. We'll reply as soon as we can."),
});

/**
 * How a language is named in a switcher (docs/i18n.md §7): its own name
 * (`हिन्दी`), its English name (`Hindi`), its code (`HI`), or both native name
 * and code. Flags are deliberately not offered - a flag is a country, not a
 * language.
 */
export const LANGUAGE_DISPLAYS = ['native', 'name', 'code', 'native_code'] as const;

export type LanguageDisplay = (typeof LANGUAGE_DISPLAYS)[number];

/**
 * A language switcher placed anywhere a section can go. It renders nothing on
 * a shop with a single language. `display` blank follows the theme's
 * `language_switcher.display`.
 */
export const languageSelectorSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('language_selector'),
  title: optionalText,
  display: z.enum(LANGUAGE_DISPLAYS).nullish().transform((value) => value ?? null),
});

export const newsletterSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('newsletter'),
  title: z.string().default('Stay in the loop'),
  subtitle: optionalText,
  button_label: z.string().default('Subscribe'),
  // `none` - and every signup saved before photos existed - is the centred form.
  image_side: z.enum(NEWSLETTER_IMAGE_SIDES).default('none'),
  image_url: optionalText,
  image_focal: imageFocal,
  second_image_url: optionalText,
  second_image_focal: imageFocal,
  /** A name field before the address. Off - every signup saved before - is email only. */
  collect_name: z.boolean().default(false),
});

/**
 * A ticking clock to the end of a sale. Mirrors `CountdownSection` in
 * `app/schemas/store.py`.
 *
 * `ends_at` is an instant (an ISO string, written in UTC by the customiser,
 * whose date and time fields are read in the store's timezone). `null` means
 * "not configured" - the theme builder never sets one - and the storefront
 * draws nothing for it on the live shop, a prompt in the customiser and a demo
 * clock in a theme's static preview. A string that is not a time reads as null.
 */
export const COUNTDOWN_STYLES = ['banner', 'boxed', 'inline'] as const;
export const COUNTDOWN_EXPIRED_BEHAVIORS = ['hide', 'show_message'] as const;
/** One picture `beside` the clock, or two `flanking` a centred one. */
export const COUNTDOWN_IMAGE_LAYOUTS = ['beside', 'flanking'] as const;

export type CountdownStyle = (typeof COUNTDOWN_STYLES)[number];
export type CountdownExpiredBehavior = (typeof COUNTDOWN_EXPIRED_BEHAVIORS)[number];

export const countdownSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('countdown'),
  title: z.string().default('Sale ends soon'),
  subtitle: optionalText,
  ends_at: z
    .string()
    .nullish()
    .transform((value) => (value && !Number.isNaN(Date.parse(value)) ? value : null)),
  cta_label: optionalText.default('Shop the sale'),
  href: optionalText,
  style: z.enum(COUNTDOWN_STYLES).catch('banner').default('banner'),
  show_days: z.boolean().default(true),
  expired_behavior: z.enum(COUNTDOWN_EXPIRED_BEHAVIORS).catch('hide').default('hide'),
  expired_message: z.string().default('This sale has ended.'),
  /** Beside the clock - or, `flanking`, the picture on its left. */
  image_url: optionalText,
  image_focal: imageFocal,
  image_layout: z.enum(COUNTDOWN_IMAGE_LAYOUTS).catch('beside').default('beside'),
  /** The picture on the clock's right, drawn only when `flanking`. */
  second_image_url: optionalText,
  second_image_focal: imageFocal,
});

/**
 * A height-and-weight form that works out a shopper's BMI in their browser -
 * nothing they type is stored or sent. Mirrors `BmiCalculatorSection` in
 * `app/schemas/store.py`. The `message_*` lines are the merchant's own words
 * under each result.
 */
export const BMI_UNITS = ['metric', 'imperial'] as const;
export const BMI_IMAGE_SIDES = ['none', 'left', 'right'] as const;

export type BmiUnits = (typeof BMI_UNITS)[number];
export type BmiImageSide = (typeof BMI_IMAGE_SIDES)[number];

export const bmiCalculatorSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('bmi_calculator'),
  title: z.string().default('Check your BMI'),
  subtitle: optionalText,
  units: z.enum(BMI_UNITS).catch('metric').default('metric'),
  image_url: optionalText,
  image_focal: imageFocal,
  image_side: z.enum(BMI_IMAGE_SIDES).catch('right').default('right'),
  cta_label: optionalText,
  href: optionalText,
  note: optionalText.default('BMI is a general guide for adults, not medical advice.'),
  message_under: optionalText,
  message_healthy: optionalText,
  message_over: optionalText,
  message_obese: optionalText,
});

/**
 * A thin band of short benefits scrolling sideways without end. Mirrors
 * `MarqueeSection`. Left off the shop until an item has words.
 */
export const MARQUEE_SPEEDS = ['slow', 'normal', 'fast'] as const;
export const MARQUEE_DIRECTIONS = ['left', 'right'] as const;
export const MARQUEE_SEPARATORS = ['dot', 'star', 'none'] as const;
export const MARQUEE_SIZES = ['sm', 'md', 'lg'] as const;

export type MarqueeSpeed = (typeof MARQUEE_SPEEDS)[number];
export type MarqueeDirection = (typeof MARQUEE_DIRECTIONS)[number];
export type MarqueeSeparator = (typeof MARQUEE_SEPARATORS)[number];
export type MarqueeSize = (typeof MARQUEE_SIZES)[number];

export const marqueeItemBlockSchema = z.object({
  ...elementBase,
  text: z.string().default(''),
  icon: z.enum(FEATURE_ICONS).nullish().catch(null).transform((value) => value ?? null),
});

export const marqueeSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('marquee'),
  speed: z.enum(MARQUEE_SPEEDS).catch('normal').default('normal'),
  direction: z.enum(MARQUEE_DIRECTIONS).catch('left').default('left'),
  separator: z.enum(MARQUEE_SEPARATORS).catch('dot').default('dot'),
  size: z.enum(MARQUEE_SIZES).catch('md').default('md'),
  uppercase: z.boolean().default(false),
  pause_on_hover: z.boolean().default(true),
  blocks: z.array(marqueeItemBlockSchema).default([]),
});

/**
 * The newest posts from the shop's blog. Mirrors `BlogPostsSection`.
 *
 * `blog` is a blog's handle, blank for every blog. The posts are read live, so
 * the section only holds how they look; it renders nothing while there are
 * none, or while the blog is switched off in the store's features.
 */
export const BLOG_POSTS_LAYOUTS = ['grid', 'list', 'featured', 'lead_grid'] as const;
export type BlogPostsLayout = (typeof BLOG_POSTS_LAYOUTS)[number];

/** How the blog's index lays its posts out. Mirrors `BlogIndexSection.layout`. */
export const BLOG_INDEX_LAYOUTS = ['theme', 'grid', 'featured_sidebar', 'list'] as const;

export const blogPostsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('blog_posts'),
  title: z.string().default('From the journal'),
  subtitle: optionalText,
  blog: optionalText,
  // Caught rather than rejected: a hand-edited 10 costs the merchant their
  // count, not the section.
  limit: z.number().int().min(2).max(6).catch(3).default(3),
  layout: z.enum(BLOG_POSTS_LAYOUTS).catch('grid').default('grid'),
  show_excerpt: z.boolean().default(true),
  show_date: z.boolean().default(true),
  show_image: z.boolean().default(true),
  cta_label: optionalText.default('Read the journal'),
});

/**
 * Appointments: what the business does, when it is free, and a way to book.
 *
 * The section a doctor, a salon, a spa or a consultant builds their shop
 * around, and the one place a shopper needs: the services, a calendar of real
 * free times, and the form that claims one.
 *
 * **What is here and what is not.** Services and opening hours are settings on
 * this section, because they are part of what the merchant designs alongside
 * the words above them. The appointments are rows on the server - which is
 * also what decides whether a time is free, so nothing in this file computes
 * availability for a shopper. The storefront asks; see
 * `api/storefront.ts::fetchBookingAvailability`.
 *
 * **A section is a calendar.** Availability is keyed by section id, so a salon
 * running haircuts on its home page and bridal trials on a landing page keeps
 * two diaries. Two sections sharing one diary would have to be one section on
 * two pages, which is a copy - a real limitation, stated rather than hidden.
 */
export const BOOKING_STAFF_MODES = ['none', 'choose', 'fixed'] as const;
export type BookingStaffMode = (typeof BOOKING_STAFF_MODES)[number];

/** The steps a step-by-step booking form can have. Mirrors `BookingStepKind` on the server. */
export const BOOKING_STEP_KINDS = ['service', 'person', 'datetime', 'details', 'review'] as const;
export type BookingStepKind = (typeof BOOKING_STEP_KINDS)[number];
export const BOOKING_QUESTION_KINDS = ['text', 'textarea', 'select', 'checkbox'] as const;
export type BookingQuestionKind = (typeof BOOKING_QUESTION_KINDS)[number];

export const bookingStepSchema = z.object({
  ...elementBase,
  kind: z.enum(BOOKING_STEP_KINDS),
  label: z
    .string()
    .nullish()
    .transform((value) => (value && value.trim() ? value : null)),
  visible: z.boolean().default(true),
});
export type BookingStep = z.infer<typeof bookingStepSchema>;

export const bookingQuestionSchema = z.object({
  ...elementBase,
  label: z.string(),
  kind: z.enum(BOOKING_QUESTION_KINDS).catch('text').default('text'),
  options: z.array(z.string()).default([]),
  required: z.boolean().default(false),
  visible: z.boolean().default(true),
});
export type BookingQuestion = z.infer<typeof bookingQuestionSchema>;

export function defaultBookingSteps(): BookingStep[] {
  return BOOKING_STEP_KINDS.map((kind) => bookingStepSchema.parse({ kind }));
}

export const bookingsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('bookings'),
  title: z.string().default('Book an appointment'),
  subtitle: optionalText,
  // The services. On a store whose services live in the `/services` catalog
  // the backend writes these from that catalog every time it serves the
  // config, so they are read-only here: the customizer picks which services
  // show (`service_ids`) and the panel page edits the services themselves.
  blocks: z.array(bookingServiceSchema).default([]),
  /**
   * Which catalog services this section offers, in catalog order. Empty is
   * every active service - the default, so a new service is bookable without a
   * second trip to the customizer. Mirrors `BookingsSection.service_ids` on
   * the server.
   */
  service_ids: z.array(z.string()).default([]),
  columns: z.number().default(3),
  show_price: z.boolean().default(true),
  show_duration: z.boolean().default(true),
  // A weekday missing from this list is shut, which is what lets a document
  // written before a merchant thought about Sundays mean the safe thing.
  hours: z.array(bookingHoursSchema).default(defaultBookingHours),
  /**
   * Whether this section keeps its own `hours` rather than the store's.
   *
   * Off - the default - the section offers the store's appointment hours,
   * kept on the `/services` page: the backend pours them into `hours` every
   * time it serves the config, so `hours` is still what the slots read, but
   * anything typed into it here would be replaced on the next load. On, the
   * section's own week stands - a bridal diary that opens on Sundays beside
   * the everyday one. Mirrors `BookingsSection.custom_hours` on the server.
   */
  custom_hours: z.boolean().default(false),
  slot_interval_minutes: z.number().default(30),
  capacity_per_slot: z.number().default(1),
  buffer_minutes: z.number().default(0),
  lead_time_hours: z.number().default(2),
  booking_window_days: z.number().default(30),
  ask_phone: z.boolean().default(true),
  ask_notes: z.boolean().default(true),
  notes_label: z.string().default('Anything we should know?'),
  button_label: z.string().default('Request appointment'),
  // Worded as a request rather than a confirmation, because an appointment
  // arrives PENDING and a shopper who reads "confirmed" turns up regardless.
  success_message: z
    .string()
    .default("Thanks - your appointment request is with us. We'll confirm it shortly."),
  /**
   * Whether an appointment is with somebody in particular.
   *
   * `none` - the default, and every section saved before people existed - is
   * exactly the old behaviour: `capacity_per_slot` seats, nobody named.
   * `choose` puts a picker in front of the calendar: each visible person who
   * offers the chosen service is one seat on their own hours, and
   * `capacity_per_slot` stops meaning anything. `fixed` books every
   * appointment with `staff_member_id` - a one-doctor clinic that still wants
   * its doctor's name on the confirmation. Mirrors `BookingsSection` on the
   * server, which is what actually enforces "one person, one appointment".
   */
  staff_mode: z.enum(BOOKING_STAFF_MODES).catch('none').default('none'),
  /** The person every booking is with, in `fixed` mode. */
  staff_member_id: z
    .string()
    .nullish()
    .transform((value) => value || null),
  /** Offers "Any available" beside the people, in `choose` mode. */
  allow_any_staff: z.boolean().default(true),
  /** The picker's heading; null is "Choose a {singular}" in the shop's words. */
  staff_label: z
    .string()
    .nullish()
    .transform((value) => (value && value.trim() ? value : null)),
  form_layout: z.enum(['single', 'steps']).catch('single').default('single'),
  steps: z.array(bookingStepSchema).catch(defaultBookingSteps).default(defaultBookingSteps),
  show_progress: z.boolean().default(true),
  show_summary: z.boolean().default(true),
  summary_position: z.enum(['side', 'bottom']).catch('side').default('side'),
  date_picker: z.enum(['calendar', 'strip', 'weeks']).catch('calendar').default('calendar'),
  questions: z.array(bookingQuestionSchema).catch([]).default([]),
});

/**
 * The people of a service business: a doctor's qualifications, a stylist's
 * speciality, and a button to book with them.
 *
 * Like the store locator, the people are not here. They are rows the merchant
 * keeps on the Team screen, read live from `/storefront/team-members`, and the
 * section holds only how they are shown - so it renders nothing while there is
 * nobody. Each card's name opens `/team/{slug}`; its button goes to
 * `{book_href}?member={slug}`, which a bookings section on that page reads to
 * preselect the person. Mirrors `TeamSection` in `backend/app/schemas/store.py`.
 */
export const TEAM_CARD_STYLES = ['portrait', 'round', 'overlay'] as const;
export type TeamCardStyle = (typeof TEAM_CARD_STYLES)[number];

/**
 * The filters a team section can offer over its people, each built from the
 * people's own facts (see `team.ts::teamFilterOptions`). A filter whose facts
 * no shown person has is not drawn at all.
 */
export const TEAM_FILTER_KINDS = [
  'speciality',
  'availability',
  'visit_format',
  'experience',
  'gender',
  'language',
] as const;
export type TeamFilterKind = (typeof TEAM_FILTER_KINDS)[number];

/** Where a team section's filters stand: a bar over the grid, or a column beside it. */
export const TEAM_FILTER_LAYOUTS = ['top', 'sidebar'] as const;
export type TeamFilterLayout = (typeof TEAM_FILTER_LAYOUTS)[number];

export const teamSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('team'),
  title: z.string().default('Meet the team'),
  subtitle: optionalText,
  // Caught rather than rejected: a hand-edited 6 costs the merchant their
  // column count, not the section.
  columns: z.number().int().min(2).max(4).catch(3).default(3),
  card_style: z.enum(TEAM_CARD_STYLES).catch('portrait').default('portrait'),
  show_bio: z.boolean().default(true),
  show_book_button: z.boolean().default(true),
  book_label: z.string().default('Book now'),
  book_href: z.string().default('/pages/book'),
  /**
   * Speciality chips over the grid - the original, single filter. Still read:
   * `true` with an empty `filters` means `['speciality']` (see
   * `team.ts::teamFilterKinds`), so a section saved before `filters` existed
   * keeps its chips.
   */
  show_filter: z.boolean().default(false),
  /**
   * The filters over the grid, in the order they are drawn. An unknown kind is
   * dropped on its own and a repeat once, so a hand-edited list loses the bad
   * entry rather than the section.
   */
  filters: z
    .array(z.string())
    .catch([])
    .default([])
    .transform((values) =>
      values.filter(
        (value, index): value is TeamFilterKind =>
          (TEAM_FILTER_KINDS as readonly string[]).includes(value) && values.indexOf(value) === index,
      ),
    ),
  /** A search box over the grid: name, speciality and role. */
  show_search: z.boolean().default(false),
  /**
   * Where the search and filters stand. `top` is the original bar of
   * single-choice chips over the grid, drawn exactly as before; `sidebar` is a
   * left column from `lg` up whose filters are checkbox groups (ticks in one
   * group widen it, groups narrow each other - `team.ts::filterTeamMulti`).
   * Caught, so a hand-edited value costs the layout and not the section.
   */
  filter_layout: z.enum(TEAM_FILTER_LAYOUTS).catch('top').default('top'),
  /** "Available today" / "Next available: Tue 14 Oct" on each card. */
  show_availability: z.boolean().default(false),
  /** A "View profile" outline button beside the book button. */
  show_profile_button: z.boolean().default(false),
  /** How many people to show; null is everyone. */
  limit: z
    .number()
    .int()
    .min(1)
    .max(48)
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
});

/**
 * The shop's newest open positions, with a link to the whole `/careers` page.
 *
 * Like the team, the roles are not here: they are rows the merchant keeps on
 * the Careers screen, read live from `/storefront/careers`. The section holds
 * only how many to show and the words around them, and renders nothing while
 * there are none - or while the Careers page is switched off. Mirrors
 * `CareersSection` in `backend/app/schemas/store.py`.
 */
export const careersSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('careers'),
  title: z.string().default('Join our team'),
  subtitle: optionalText,
  // Caught rather than rejected: a hand-edited 50 costs the merchant their
  // count, not the section.
  limit: z.number().int().min(1).max(12).catch(3).default(3),
  show_summary: z.boolean().default(true),
  /** The "View all openings" link under the list; blank is the shop's own words. */
  cta_label: optionalText,
});

/**
 * The shop's physical branches: addresses, hours, "open now" and directions.
 *
 * The addresses are not here - they are rows the merchant keeps on the Store
 * locations screen, read live from `/storefront/locations`. The section holds
 * only how they are shown, and renders nothing while there are none. Mirrors
 * `StoreLocatorSection` in `backend/app/schemas/store.py`.
 */
export const STORE_LOCATOR_LAYOUTS = ['list', 'cards', 'list_with_map'] as const;
export type StoreLocatorLayout = (typeof STORE_LOCATOR_LAYOUTS)[number];

export const storeLocatorSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('store_locator'),
  title: z.string().default('Find a store'),
  subtitle: optionalText,
  layout: z.enum(STORE_LOCATOR_LAYOUTS).catch('list').default('list'),
  show_hours: z.boolean().default(true),
  show_phone: z.boolean().default(true),
  show_directions: z.boolean().default(true),
  search: z.boolean().default(false),
});

/**
 * What shoppers who bought the product said about it.
 *
 * Unlike `testimonials`, nothing here is the merchant's writing: the reviews
 * are rows on the server, written by signed-in customers and moderated in the
 * dashboard. The section only decides how they are shown. It belongs on the
 * product page, where there is a product to review - anywhere else it renders
 * nothing, because a list of reviews of no product in particular is not one.
 * Mirrors `ProductReviewsSection` in `backend/app/schemas/store.py`.
 */
export const REVIEW_SORTS = ['newest', 'highest', 'lowest', 'photos'] as const;
export const REVIEW_LAYOUTS = ['list', 'grid'] as const;

export type ReviewSort = (typeof REVIEW_SORTS)[number];
export type ReviewLayout = (typeof REVIEW_LAYOUTS)[number];

export const productReviewsSectionSchema = z.object({
  ...sectionBase,
  type: z.literal('product_reviews'),
  title: optionalText.default('Customer reviews'),
  subtitle: optionalText,
  // Caught rather than rejected: a hand-edited 50 should cost the merchant
  // their page size, not the whole section.
  page_size: z.number().int().min(3).max(20).catch(5),
  default_sort: z.enum(REVIEW_SORTS).default('newest'),
  show_breakdown: z.boolean().default(true),
  show_photos: z.boolean().default(true),
  layout: z.enum(REVIEW_LAYOUTS).default('list'),
});

// --- Page anchors ---------------------------------------------------------
/**
 * The section that *is* a page.
 *
 * A product page without its product, a cart without its lines, is not a page
 * anyone meant to build - so every page but the home page owns one section it
 * cannot be deleted, hidden or duplicated out of. What an anchor renders is not
 * markup this module holds: it is the page's own content, handed to the
 * renderer as a slot. That is what lets the cart keep its cart logic and the
 * product page keep the theme's arrangement, while both gain sections above and
 * below them.
 *
 * Every setting on an anchor is nullable, or `'theme'`, and both mean "whatever
 * this theme already does". A merchant who never opens the inspector therefore
 * gets the theme's own density, filters and panels untouched, and one who does
 * overrides exactly the parts they touched.
 */

/** An anchor setting the merchant has left at the theme's own value. */
const inheritedNumber = z.number().nullish().transform((value) => value ?? null);
const inheritedBoolean = z.boolean().nullish().transform((value) => value ?? null);

/** `'theme'` on any of these means what `null` means on an inherited number. */
const PANEL_STYLES = ['theme', 'filled', 'outlined', 'bare'] as const;
const CARD_PRESENTATIONS = ['theme', 'standard', 'framed', 'airy', 'overlay'] as const;
const FILTER_PLACEMENTS = ['theme', 'chips', 'sidebar', 'inline'] as const;
const CATEGORY_TILES = ['theme', 'card', 'overlay', 'bare'] as const;

export type PanelChoice = (typeof PANEL_STYLES)[number];
export type CardChoice = (typeof CARD_PRESENTATIONS)[number];
export type FilterChoice = (typeof FILTER_PLACEMENTS)[number];
export type CategoryTileChoice = (typeof CATEGORY_TILES)[number];

/**
 * Anchors default to no padding of their own. The page bodies they stand for
 * already carry the spacing their theme designed, and a second helping of it
 * is a gap no merchant asked for.
 */
const anchorBase = {
  ...sectionBase,
  padding: z.enum(SECTION_PADDINGS).default('none'),
  /**
   * The components inside this page, in the order they stand.
   *
   * Empty - the default, and what every theme saved before parts existed
   * parses to - means "nobody has arranged this page", and resolves to the
   * arrangement the page has always rendered. See `partDocument.ts`, which is
   * the only module that should read this field: everything else asks it what
   * the page is made of and gets an answer that is right for an untouched shop
   * and for an arranged one alike.
   */
  parts: z.array(pagePartSchema).max(MAX_PARTS_PER_ANCHOR).default([]),
};

/**
 * Where a service's product page offers its booking calendar.
 *
 * `theme` (the default, as on every anchor - no theme draws one today) and
 * `off` are the page every theme draws: the pictures and the buy box.
 * `sidebar` is for a service business, whose "product" is an appointment: the
 * theme's buy box gives way to a sticky booking card (`BookingPanel`) beside
 * the pictures and the description, started on the bookings service named
 * like the product. A shop with no bookings section keeps its buy box whatever
 * this says - a page with no way to buy and no way to book is a dead end.
 */
export const PRODUCT_BOOKING_PANELS = ['theme', 'off', 'sidebar'] as const;
export type ProductBookingPanel = (typeof PRODUCT_BOOKING_PANELS)[number];

export const productDetailsSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('product_details'),
  /** Null follows the theme's own `productRails`. */
  show_related: inheritedBoolean,
  booking_panel: z.enum(PRODUCT_BOOKING_PANELS).catch('theme').default('theme'),
});

export const productListSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('product_list'),
  /** Blank keeps the page's own heading: the category's name, or "All products". */
  title: optionalText,
  columns: inheritedNumber,
  filters: z.enum(FILTER_PLACEMENTS).default('theme'),
  card: z.enum(CARD_PRESENTATIONS).default('theme'),
  page_size: inheritedNumber,
  show_search_link: z.boolean().default(true),
});

export const categoryListSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('category_list'),
  title: optionalText,
  columns: inheritedNumber,
  tile: z.enum(CATEGORY_TILES).default('theme'),
});

export const searchResultsSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('search_results'),
  title: optionalText,
  columns: inheritedNumber,
  card: z.enum(CARD_PRESENTATIONS).default('theme'),
  panel: z.enum(PANEL_STYLES).default('theme'),
  show_filters: z.boolean().default(true),
});

export const cartLinesSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('cart_lines'),
  title: optionalText,
  panel: z.enum(PANEL_STYLES).default('theme'),
  empty_message: optionalText,
});

export const checkoutFormSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('checkout_form'),
  title: optionalText,
  panel: z.enum(PANEL_STYLES).default('theme'),
});


/**
 * The pages a shopper reaches when they are not shopping.
 *
 * Sign-in, the account, the writing and the dead end all render content this
 * module does not own - a form with real validation, an order history, a post
 * from the merchant's blog - so, like every other anchor, they are a position
 * in the merchant's stack rather than markup. What each one gains by being
 * arrangeable is the band above and the band below: reassurance over a sign-in
 * form, a newsletter under a post, and a 404 that offers a way back into the
 * shop instead of an apology.
 *
 * Their settings are the smallest set that is honest. Every one of them is
 * blank by default and every blank keeps the page's own words, so a merchant
 * who never opens these pages has a shop that reads exactly as it read before
 * they were arrangeable.
 */
export const customerLoginSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('customer_login'),
  /** Blank keeps "Sign in". */
  title: optionalText,
  /** Blank keeps the shop's own line about why an account is worth having. */
  intro: optionalText,
});

export const customerRegisterSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('customer_register'),
  title: optionalText,
  intro: optionalText,
});

export const customerAccountSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('customer_account'),
  title: optionalText,
});

export const blogIndexSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('blog_index'),
  /** Blank keeps the blog's own title, which is the merchant's and per-blog. */
  title: optionalText,
  /**
   * `theme` (the default, as on every anchor) and `grid` - the cards the index
   * has always drawn; `featured_sidebar` - a large
   * lead story, a column of featured articles beside it and the rest as
   * horizontal rows; `list` - horizontal rows only.
   */
  layout: z.enum(BLOG_INDEX_LAYOUTS).catch('theme').default('theme'),
});

export type BlogIndexLayout = (typeof BLOG_INDEX_LAYOUTS)[number];

export const blogPostSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('blog_post'),
});

/**
 * One person's profile, at `/team/{slug}`: photo, name, role, qualifications,
 * bio, hours, the services they offer, a fee note and a way to book them.
 *
 * Unlike the other anchors its settings are not inherited from a theme - no
 * theme drew this page before it existed - so they carry plain defaults, and
 * a merchant who never opens the inspector gets the whole profile.
 */
export const TEAM_PROFILE_LAYOUTS = ['theme', 'stacked', 'tabs', 'sidebar'] as const;
export type TeamProfileLayout = (typeof TEAM_PROFILE_LAYOUTS)[number];

export const teamMemberDetailsSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('team_member_details'),
  /**
   * `stacked` draws every block one under another; `tabs` puts them behind
   * About / Education & career / Services / Availability / Book tabs under the
   * header. `theme` is the theme's choice, which today is stacked everywhere -
   * the value exists so a theme can later choose tabs for merchants who never
   * picked. `sidebar` draws the stacked blocks in a column about two thirds
   * wide with this person's booking card (`BookingPanel`) sticky beside them;
   * a person no bookings section can book gets the stacked page, full width.
   */
  layout: z.enum(TEAM_PROFILE_LAYOUTS).catch('theme').default('theme'),
  show_qualifications: z.boolean().default(true),
  show_hours: z.boolean().default(true),
  show_services: z.boolean().default(true),
  /** "12 years experience" in the header. */
  show_experience: z.boolean().default(true),
  /** The languages they speak, as chips in the header. */
  show_languages: z.boolean().default(true),
  show_education: z.boolean().default(true),
  show_career: z.boolean().default(true),
  /**
   * A booking calendar for this person, embedded on their page: the first
   * bookings section in the theme that books by person and can book them.
   */
  show_booking_calendar: z.boolean().default(true),
  show_book_button: z.boolean().default(true),
  book_label: z.string().default('Book an appointment'),
  book_href: z.string().default('/pages/book'),
});

export const notFoundSectionSchema = z.object({
  ...anchorBase,
  type: z.literal('not_found'),
  title: optionalText,
  body: optionalText,
  cta_label: optionalText,
  cta_href: optionalText,
});

export const sectionSchema = z.discriminatedUnion('type', [
  heroSectionSchema,
  slideshowSectionSchema,
  featuredProductsSectionSchema,
  recentlyViewedSectionSchema,
  productTabsSectionSchema,
  categoriesSectionSchema,
  categoryStripSectionSchema,
  imageWithTextSectionSchema,
  bannerSectionSchema,
  textSectionSchema,
  linkDirectorySectionSchema,
  featuresSectionSchema,
  testimonialsSectionSchema,
  faqSectionSchema,
  timelineSectionSchema,
  gallerySectionSchema,
  promoGridSectionSchema,
  brandStripSectionSchema,
  lookbookSectionSchema,
  beforeAfterSectionSchema,
  pricingPlansSectionSchema,
  productSpotlightSectionSchema,
  productShowcaseSectionSchema,
  videoSectionSchema,
  socialLinksSectionSchema,
  contactFormSectionSchema,
  newsletterSectionSchema,
  countdownSectionSchema,
  bmiCalculatorSectionSchema,
  marqueeSectionSchema,
  blogPostsSectionSchema,
  languageSelectorSectionSchema,
  bookingsSectionSchema,
  storeLocatorSectionSchema,
  teamSectionSchema,
  careersSectionSchema,
  productReviewsSectionSchema,
  productDetailsSectionSchema,
  productListSectionSchema,
  categoryListSectionSchema,
  searchResultsSectionSchema,
  cartLinesSectionSchema,
  checkoutFormSectionSchema,
  customerLoginSectionSchema,
  customerRegisterSectionSchema,
  customerAccountSectionSchema,
  blogIndexSectionSchema,
  blogPostSectionSchema,
  teamMemberDetailsSectionSchema,
  notFoundSectionSchema,
]);

export type Section = z.infer<typeof sectionSchema>;
export type SectionType = Section['type'];
export type HeroSection = z.infer<typeof heroSectionSchema>;
export type SlideshowSection = z.infer<typeof slideshowSectionSchema>;
export type FeaturedProductsSection = z.infer<typeof featuredProductsSectionSchema>;
export type RecentlyViewedSection = z.infer<typeof recentlyViewedSectionSchema>;
export type ProductTabsSection = z.infer<typeof productTabsSectionSchema>;
export type ProductTabBlock = z.infer<typeof productTabBlockSchema>;
export type CategoriesSection = z.infer<typeof categoriesSectionSchema>;
export type CategoryStripSection = z.infer<typeof categoryStripSectionSchema>;
export type ImageWithTextSection = z.infer<typeof imageWithTextSectionSchema>;
export type BannerSection = z.infer<typeof bannerSectionSchema>;
export type TextSection = z.infer<typeof textSectionSchema>;
export type LinkDirectorySection = z.infer<typeof linkDirectorySectionSchema>;
export type LinkDirectoryBlock = z.infer<typeof linkDirectoryBlockSchema>;
export type LinkDirectoryLink = z.infer<typeof linkDirectoryLinkSchema>;
export type FeaturesSection = z.infer<typeof featuresSectionSchema>;
export type TestimonialsSection = z.infer<typeof testimonialsSectionSchema>;
export type FaqSection = z.infer<typeof faqSectionSchema>;
export type TimelineSection = z.infer<typeof timelineSectionSchema>;
export type GallerySection = z.infer<typeof gallerySectionSchema>;
export type PromoGridSection = z.infer<typeof promoGridSectionSchema>;
export type BrandStripSection = z.infer<typeof brandStripSectionSchema>;
export type LookbookSection = z.infer<typeof lookbookSectionSchema>;
export type BeforeAfterSection = z.infer<typeof beforeAfterSectionSchema>;
export type PricingPlansSection = z.infer<typeof pricingPlansSectionSchema>;
export type PricingPlanBlock = z.infer<typeof pricingPlanBlockSchema>;
export type ProductSpotlightSection = z.infer<typeof productSpotlightSectionSchema>;
export type ProductShowcaseSection = z.infer<typeof productShowcaseSectionSchema>;
export type VideoSection = z.infer<typeof videoSectionSchema>;
export type SocialLinksSection = z.infer<typeof socialLinksSectionSchema>;
export type SocialLinkBlock = z.infer<typeof socialLinkBlockSchema>;
export type ContactFormSection = z.infer<typeof contactFormSectionSchema>;
export type NewsletterSection = z.infer<typeof newsletterSectionSchema>;
export type CountdownSection = z.infer<typeof countdownSectionSchema>;
export type BmiCalculatorSection = z.infer<typeof bmiCalculatorSectionSchema>;
export type MarqueeSection = z.infer<typeof marqueeSectionSchema>;
export type MarqueeItemBlock = z.infer<typeof marqueeItemBlockSchema>;
export type BlogPostsSection = z.infer<typeof blogPostsSectionSchema>;
export type LanguageSelectorSection = z.infer<typeof languageSelectorSectionSchema>;
export type BookingsSection = z.infer<typeof bookingsSectionSchema>;
export type StoreLocatorSection = z.infer<typeof storeLocatorSectionSchema>;
export type TeamSection = z.infer<typeof teamSectionSchema>;
export type CareersSection = z.infer<typeof careersSectionSchema>;
export type TeamMemberDetailsSection = z.infer<typeof teamMemberDetailsSectionSchema>;
export type ProductReviewsSection = z.infer<typeof productReviewsSectionSchema>;
export type ProductDetailsSection = z.infer<typeof productDetailsSectionSchema>;
export type ProductListSection = z.infer<typeof productListSectionSchema>;
export type CategoryListSection = z.infer<typeof categoryListSectionSchema>;
export type SearchResultsSection = z.infer<typeof searchResultsSectionSchema>;
export type CartLinesSection = z.infer<typeof cartLinesSectionSchema>;
export type CheckoutFormSection = z.infer<typeof checkoutFormSectionSchema>;
export type CustomerLoginSection = z.infer<typeof customerLoginSectionSchema>;
export type CustomerRegisterSection = z.infer<typeof customerRegisterSectionSchema>;
export type CustomerAccountSection = z.infer<typeof customerAccountSectionSchema>;
export type BlogIndexSection = z.infer<typeof blogIndexSectionSchema>;
export type BlogPostSection = z.infer<typeof blogPostSectionSchema>;
export type NotFoundSection = z.infer<typeof notFoundSectionSchema>;

/**
 * The one anchor fact that needs the section union to state it. The list of
 * anchor types itself lives in `primitives.ts`, because the part registry is
 * keyed by it and cannot import this module.
 */
export type AnchorSection = Extract<Section, { type: AnchorSectionType }>;

export function isAnchor(section: Section): section is AnchorSection {
  return isAnchorType(section.type);
}

/** Sections that hold repeatable blocks, and what one block is called. */
export type BlockSection =
  | SlideshowSection
  | ProductTabsSection
  | FeaturesSection
  | TestimonialsSection
  | FaqSection
  | LinkDirectorySection
  | TimelineSection
  | GallerySection
  | PromoGridSection
  | BrandStripSection
  | LookbookSection
  | SocialLinksSection
  | BookingsSection
  | PricingPlansSection
  | MarqueeSection;

export function hasBlocks(section: Section): section is BlockSection {
  return 'blocks' in section;
}

/**
 * Every section in the document that this build can render, in order.
 *
 * A section that fails to parse is skipped and logged rather than thrown: the
 * shopper's page is worth more than strictness about one section a merchant
 * cannot even see is broken.
 */
export function parseSections(raw: unknown): Section[] {
  if (!Array.isArray(raw)) return [];

  const sections: Section[] = [];
  for (const candidate of raw) {
    const parsed = sectionSchema.safeParse(candidate);
    if (parsed.success) sections.push(parsed.data);
  }
  return sections;
}

// --- Rows -----------------------------------------------------------------
/**
 * Sections that run the width of the page whatever else is on it.
 *
 * A hero and a slideshow are the shop's banner: they carry their own full-bleed
 * artwork and their own `width` setting for it, and half of one beside a
 * contact form is not a thing anyone wants. The builder does not offer them a
 * span, and this is the guarantee behind that - a document that somehow
 * carries one still renders the banner full width rather than a broken half.
 */
export function spansPageWidth(type: SectionType): boolean {
  // Anchors are the page itself; half a cart beside a newsletter is not a
  // thing to offer, so they are excluded from rows for the same reason a hero
  // is - and, like the hero, the guarantee lives here rather than only in the
  // builder, so a hand-edited document still renders a whole page.
  return type === 'hero' || type === 'slideshow' || isAnchorType(type);
}

/**
 * Whether a section is offered a content width. Not the banners - they bleed
 * to the window edges by design - and not a page's own anchor, whose layout is
 * the theme's.
 */
export function takesContentWidth(type: SectionType): boolean {
  return !spansPageWidth(type);
}

/**
 * The content width a section renders at on one screen, in percent, or `null`
 * for Fit. A tablet follows the desktop: it is the phone that needs its own.
 */
export function contentWidthFor(section: Section, device: SectionDevice): number | null {
  if (!takesContentWidth(section.type)) return null;
  return device === 'mobile' ? section.content_width_mobile : section.content_width;
}

/** The span a section actually renders at. */
export function sectionSpan(section: Section): SectionSpan {
  return spansPageWidth(section.type) ? 'full' : section.column_span;
}

/** How the spans are named to a merchant, who does not think in twelfths. */
export const SECTION_SPAN_LABELS: Record<SectionSpan, string> = {
  full: 'Full width',
  two_thirds: 'Two thirds',
  half: 'Half',
  third: 'One third',
};

/**
 * The page as rows, each row the sections that stand side by side in it.
 *
 * Grouping is positional and greedy: a narrow section opens a row and the
 * sections after it join while they fit, so "put a picture next to the contact
 * form" is done by making both of them half and leaving them next to each
 * other in the list. There is no container to nest a section inside and no
 * second structure to keep in step with the order - the order is still the
 * page, which is what makes reordering, hiding and deleting keep working
 * exactly as they did.
 *
 * A row that does not fill is not padded out: it is centred at the width its
 * sections asked for, which is how a lone half-width contact form stops being
 * a form stretched across a wide screen.
 */
export function sectionRows(sections: Section[]): Section[][] {
  const rows: Section[][] = [];
  let row: Section[] = [];
  let units = 0;

  for (const section of sections) {
    const span = SPAN_UNITS[sectionSpan(section)];

    if (span >= ROW_UNITS) {
      if (row.length > 0) rows.push(row);
      rows.push([section]);
      row = [];
      units = 0;
      continue;
    }

    if (units + span > ROW_UNITS) {
      rows.push(row);
      row = [];
      units = 0;
    }

    row.push(section);
    units += span;
  }

  if (row.length > 0) rows.push(row);
  return rows;
}

/**
 * A hero's pictures as editable rows, oldest documents included.
 *
 * A hero used to carry exactly one picture, in `image_url`, and thousands of
 * saved themes still do. Rather than backfilling them, that single picture is
 * read as a rotation of one - so a merchant opening an old hero finds their
 * image already in the list, and a hero nobody edits keeps rendering from the
 * field it always rendered from.
 *
 * The stand-in row's id is derived from the section's own, so it is the same
 * id on every render and React does not tear the row down under an upload.
 */
export function heroImageRows(section: HeroSection): HeroImage[] {
  if (section.images.length > 0) return section.images;
  return section.image_url
    ? [
        {
          id: `${section.id}-image`,
          image_url: section.image_url,
          image_focal: null,
          visible: true,
        },
      ]
    : [];
}

/** The pictures the storefront actually paints, each with where it is held - blank rows dropped. */
export function heroImageItems(section: HeroSection): HeroImage[] {
  return heroImageRows(section).filter((image) => image.visible && image.image_url);
}

/** The same, as bare URLs. */
export function heroImages(section: HeroSection): string[] {
  return heroImageItems(section).map((image) => image.image_url);
}

// --- The section library --------------------------------------------------
/**
 * The shelves the "add section" library is laid out on.
 *
 * A merchant looking for a way to show products does not know whether the shop
 * calls it a grid or a carousel, but they do know they are shopping for
 * commerce rather than for copy. The order below is the order the library
 * lists them in.
 */
export const SECTION_GROUPS = ['commerce', 'marketing', 'content', 'forms', 'page'] as const;

export type SectionGroup = (typeof SECTION_GROUPS)[number];

export const SECTION_GROUP_LABELS: Record<SectionGroup, string> = {
  commerce: 'Commerce',
  marketing: 'Marketing',
  content: 'Content',
  forms: 'Forms',
  page: 'The page itself',
};

export interface SectionSpec {
  type: SectionType;
  label: string;
  /** Which shelf of the library it is offered on. */
  group: SectionGroup;
  /** What it is for, in the words a merchant would use. */
  description: string;
  /** Singular name of one block, or null when the section holds none. */
  blockLabel: string | null;
  maxBlocks: number;
  /** True for the section that *is* a page. See {@link ANCHOR_SECTION_TYPES}. */
  anchor?: boolean;
}

/**
 * What the "add section" palette offers, in the order it offers them: the
 * things a shop needs above the fold first, the supporting sections after.
 * The order and the set both match `SECTION_TYPES` on the server.
 */
export const SECTION_LIBRARY: readonly SectionSpec[] = [
  {
    type: 'hero',
    group: 'marketing',
    label: 'Hero banner',
    description: 'A full-width welcome with a headline, one button, and a picture or a few to cycle through.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'slideshow',
    group: 'marketing',
    label: 'Slideshow',
    description: 'Several banners that rotate. One slide per campaign.',
    blockLabel: 'Slide',
    maxBlocks: 8,
  },
  {
    type: 'featured_products',
    group: 'commerce',
    label: 'Product grid',
    description: 'A row of products, all of them or one category.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'product_tabs',
    group: 'commerce',
    label: 'Product tabs',
    description: 'New arrivals, best sellers, on sale or a category - one product row, a tab for each.',
    blockLabel: 'Tab',
    maxBlocks: 5,
  },
  {
    type: 'recently_viewed',
    group: 'commerce',
    label: 'Recently viewed',
    description: 'The products each shopper looked at lately. Hidden until they have looked at some.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'categories',
    group: 'commerce',
    label: 'Category grid',
    description: 'Ways into the catalog, by category.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'category_strip',
    group: 'commerce',
    label: 'Category strip',
    description: 'One thin row of small icons linking to your categories, usually just under the header. Slides sideways on phones.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'image_with_text',
    group: 'content',
    label: 'Image with text',
    description: 'A picture beside a paragraph. Good for your story.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'gallery',
    group: 'content',
    label: 'Image gallery',
    description: 'A grid of photographs, each one optionally a link.',
    blockLabel: 'Image',
    maxBlocks: 12,
  },
  {
    type: 'promo_grid',
    group: 'marketing',
    label: 'Promo tiles',
    description: 'A mosaic of offer tiles - "Up to 50% off headphones" - each with a picture or a colour, and a link.',
    blockLabel: 'Tile',
    maxBlocks: 6,
  },
  {
    type: 'brand_strip',
    group: 'commerce',
    label: 'Shop by brand',
    description: 'A row of brand logos. With none uploaded, the brands on your products are shown by name.',
    blockLabel: 'Brand',
    maxBlocks: 16,
  },
  {
    type: 'lookbook',
    group: 'commerce',
    label: 'Shop the look',
    description: 'A styled photo with your products tagged on it. Shoppers tap a dot to see the product and its price.',
    blockLabel: 'Hotspot',
    maxBlocks: 8,
  },
  {
    type: 'before_after',
    group: 'content',
    label: 'Before and after',
    description: 'Two photos of the same thing with a slider between them - great for results, cleaning or makeovers.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'pricing_plans',
    group: 'commerce',
    label: 'Pricing plans',
    description: 'Two to four plans side by side - memberships, packages, diet or coaching plans - each with a price and a button.',
    blockLabel: 'Plan',
    maxBlocks: 4,
  },
  {
    type: 'product_spotlight',
    group: 'commerce',
    label: 'Product spotlight',
    description: 'One product shown large, with its price and an add-to-cart button beside a big picture.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'product_showcase',
    group: 'commerce',
    label: 'Product showcase',
    description: 'A dark band with a big product scene, a heading and a button, and a small product card on the picture.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'video',
    group: 'content',
    label: 'Video',
    description:
      'A YouTube video or a file of your own, in a player shoppers can play, pause, seek and expand.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'social_links',
    group: 'marketing',
    label: 'Social media links',
    // The networks are named rather than summarised, because the library is
    // searched by label and description: a merchant looking for this section
    // types "instagram" or "whatsapp", not "social links".
    description:
      'Instagram, Facebook, YouTube, WhatsApp, X, LinkedIn and Pinterest, as a row of their own icons. Paste an address and its icon appears.',
    blockLabel: 'Link',
    maxBlocks: 12,
  },
  {
    type: 'features',
    group: 'marketing',
    label: 'Promises strip',
    description: 'Delivery, returns, payments - what you offer, with icons. Or big figures, like 15+ years.',
    blockLabel: 'Promise',
    maxBlocks: 8,
  },
  {
    type: 'testimonials',
    group: 'marketing',
    label: 'Customer reviews',
    description: 'Quotes from real customers, with a rating.',
    blockLabel: 'Review',
    maxBlocks: 9,
  },
  {
    type: 'product_reviews',
    group: 'commerce',
    label: 'Product reviews',
    description:
      'Star ratings and reviews from customers who bought the product, with a form to write one. For the product page.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'faq',
    group: 'content',
    label: 'Questions & answers',
    description: 'The questions shoppers ask before they buy.',
    blockLabel: 'Question',
    maxBlocks: 12,
  },
  {
    type: 'timeline',
    group: 'content',
    label: 'Timeline',
    description: 'Your story in dated steps - when you opened, how you grew, where you are today.',
    blockLabel: 'Milestone',
    maxBlocks: 12,
  },
  {
    type: 'text',
    group: 'content',
    label: 'Text',
    description: 'A block of formatted writing on its own.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'link_directory',
    group: 'content',
    label: 'Link directory',
    description:
      'Rows of small text links under short headings - popular searches or every sub-category. Fills itself from your categories, or type your own groups.',
    blockLabel: 'Link group',
    maxBlocks: 24,
  },
  {
    type: 'banner',
    group: 'marketing',
    label: 'Announcement',
    description: 'A single line across the page - a sale, a notice, a deadline.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'contact_form',
    group: 'forms',
    label: 'Contact form',
    description: 'Let shoppers write to you. Messages arrive by email.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'newsletter',
    group: 'forms',
    label: 'Email signup',
    description: 'Collect email addresses for your marketing.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'countdown',
    group: 'marketing',
    label: 'Countdown timer',
    description: 'A ticking clock to the end of a sale, with a button to shop it. Hides itself when time is up.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'bmi_calculator',
    group: 'content',
    label: 'BMI calculator',
    description: 'A height and weight form that works out BMI, with your own line under each result and a photo beside it.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'marquee',
    group: 'marketing',
    label: 'Scrolling ribbon',
    description: 'A thin band of short benefits - free shipping, lab tested - scrolling sideways without end.',
    blockLabel: 'Item',
    maxBlocks: 12,
  },
  {
    type: 'blog_posts',
    group: 'content',
    label: 'Blog posts',
    description: 'Your newest blog posts, as cards, a list or one lead story. Hidden while there are none.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'bookings',
    group: 'commerce',
    label: 'Bookings & appointments',
    description:
      'Your services, your free times, and a form that takes the booking. For clinics, salons, spas and consultants.',
    blockLabel: 'Service',
    maxBlocks: 12,
  },
  {
    type: 'store_locator',
    group: 'content',
    label: 'Store locator',
    description:
      'Your shops people can walk into: addresses, opening hours, "open now" and directions. Hidden until you add a location.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'team',
    group: 'content',
    label: 'Team',
    description:
      'Your doctors, stylists or consultants, with a profile page each and a button to book them. Hidden until you add someone.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'careers',
    group: 'content',
    label: 'Careers',
    description:
      'Your newest open positions, with a link to your Careers page. Hidden until you add a role and turn the Careers page on.',
    blockLabel: null,
    maxBlocks: 0,
  },
  {
    type: 'language_selector',
    group: 'content',
    label: 'Language selector',
    description: 'Let shoppers read your shop in another language. Hidden while the shop has only one.',
    blockLabel: null,
    maxBlocks: 0,
  },
] as const;

/**
 * The anchors, described for the same list the merchant reads.
 *
 * Deliberately not in `SECTION_LIBRARY`: that array is the "add section"
 * palette, and an anchor is never added - it arrives with the page and stays.
 * They still need a label and a sentence, because the builder lists them
 * alongside everything else and a row reading `cart_lines` is not a row anyone
 * wrote for a merchant.
 */
export const ANCHOR_LIBRARY: readonly SectionSpec[] = [
  {
    type: 'product_details',
    group: 'page',
    label: 'Product details',
    description: "The product itself: its pictures, price, options and buy button, laid out the way this theme lays them out.",
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'product_list',
    group: 'page',
    label: 'Product listing',
    description: 'The catalog grid, its category filter and its pages.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'category_list',
    group: 'page',
    label: 'Category listing',
    description: 'Every shelf in the shop, as a grid of tiles.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'search_results',
    group: 'page',
    label: 'Search results',
    description: 'What a shopper found, with the facets they narrow it by.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'cart_lines',
    group: 'page',
    label: 'Cart',
    description: 'The basket: its lines, its discount box and its totals.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'checkout_form',
    group: 'page',
    label: 'Checkout',
    description: 'The address and payment form, with the order summary beside it.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'customer_login',
    group: 'page',
    label: 'Sign in',
    description: 'The shopper\u2019s sign-in form, with whatever you put around it.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'customer_register',
    group: 'page',
    label: 'Create an account',
    description: 'The registration form, and the case for having an account.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'customer_account',
    group: 'page',
    label: 'Account',
    description: 'A signed-in shopper\u2019s own page: their orders, addresses and details.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'blog_index',
    group: 'page',
    label: 'Blog',
    description: 'Your posts, newest first.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'blog_post',
    group: 'page',
    label: 'Blog post',
    description: 'One post, as a reader meets it.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'team_member_details',
    group: 'page',
    label: 'Team member profile',
    description: 'One person: their photo, role, qualifications, hours, services and a button to book them.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
  {
    type: 'not_found',
    group: 'page',
    label: 'Page not found',
    description: 'The dead end, and the way you offer back into the shop.',
    blockLabel: null,
    maxBlocks: 0,
    anchor: true,
  },
] as const;

export function sectionSpec(type: SectionType): SectionSpec {
  // Every member of `SectionType` is in one of the two lists by construction;
  // the fallback keeps the return type honest rather than describing a real case.
  return (
    SECTION_LIBRARY.find((spec) => spec.type === type) ??
    ANCHOR_LIBRARY.find((spec) => spec.type === type) ??
    SECTION_LIBRARY[0]
  );
}

/** How a section is titled in the builder's list: its own copy where it has any. */
export function sectionTitle(section: Section): string {
  const spec = sectionSpec(section.type);
  const own =
    'heading' in section
      ? section.heading
      : 'title' in section
        ? section.title
        : 'text' in section
          ? section.text
          : '';
  const trimmed = (own ?? '').trim();
  if (!trimmed) return spec.label;
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
}

// --- Creating -------------------------------------------------------------
export type Block = BlockSection['blocks'][number];

/** One new block for a section that holds them. */
export function createBlock(type: SectionType): Block {
  switch (type) {
    case 'slideshow':
      return slideBlockSchema.parse({ heading: 'Slide headline' });
    case 'product_tabs':
      // A category tab rather than another sort: the three sorts arrive with
      // the section, so a merchant adding a tab is adding a shelf.
      return productTabBlockSchema.parse({ label: 'A category', source: 'category' });
    case 'features':
      return featureBlockSchema.parse({ title: 'What you offer' });
    case 'testimonials':
      // Empty on purpose: a review is a thing a customer said, so there is
      // nothing honest to prefill it with.
      return testimonialBlockSchema.parse({ quote: '' });
    case 'faq':
      return faqBlockSchema.parse({ question: 'A question shoppers ask', answer: '' });
    case 'link_directory':
      return linkDirectoryBlockSchema.parse({
        heading: 'A group of links',
        links: [{ label: 'A link', href: '/products' }],
      });
    case 'timeline':
      return timelineBlockSchema.parse({ date: 'Year', title: 'What happened' });
    case 'gallery':
      return galleryBlockSchema.parse({ image_url: '' });
    case 'promo_grid':
      return promoTileBlockSchema.parse({ heading: 'An offer worth a tap', cta_label: 'Shop now', href: '/products' });
    case 'brand_strip':
      return brandBlockSchema.parse({});
    case 'lookbook':
      // The middle of the picture, with no product yet: the merchant drags it
      // where it belongs and picks what it points at.
      return lookbookHotspotBlockSchema.parse({});
    case 'social_links':
      // `custom` rather than the next unused network: the seven named ones are
      // already on the section, so a merchant reaching for "add" is reaching
      // for the one that is not in the list. They can still pick a network
      // from the dropdown - this is the first guess, not the only answer.
      return socialLinkBlockSchema.parse({ platform: 'custom' });
    case 'bookings':
      return bookingServiceSchema.parse({ name: 'A service you offer', duration_minutes: 30 });
    case 'pricing_plans':
      return pricingPlanBlockSchema.parse({ name: 'A plan', cta_label: 'Choose plan' });
    case 'marquee':
      return marqueeItemBlockSchema.parse({ text: 'A short benefit', icon: 'sparkles' });
    default:
      // Unreachable: the builder only offers "add block" for the five above.
      return slideBlockSchema.parse({});
  }
}

/**
 * The prompt a new hero arrives with in the builder. Exported so the live shop
 * can tell a hero nobody wrote from one the merchant did: the prompt is for
 * the merchant, and a shopper reading "A headline that says what you sell"
 * over a privacy policy is reading the builder (`isPlaceholderHero`).
 */
export const HERO_PLACEHOLDER = {
  heading: 'A headline that says what you sell',
  subheading: 'One line more about why it is worth buying.',
} as const;

/** Whether a hero still carries the builder's prompt as its headline. */
export function isPlaceholderHero(section: { heading: string }): boolean {
  return section.heading.trim() === HERO_PLACEHOLDER.heading;
}

/**
 * A new section of `type`, filled in enough to look like something.
 *
 * An empty section reads as a bug in the builder, so a new one arrives with
 * placeholder copy the merchant overwrites - except where the placeholder
 * would be a claim about their business rather than a label. `testimonials`
 * therefore starts with no reviews at all.
 */
export function createSection<K extends SectionType>(type: K): Extract<Section, { type: K }> {
  const drafts: { [T in SectionType]: () => Extract<Section, { type: T }> } = {
    hero: () =>
      heroSectionSchema.parse({
        type: 'hero',
        heading: HERO_PLACEHOLDER.heading,
        subheading: HERO_PLACEHOLDER.subheading,
        cta_label: 'Shop now',
        cta_href: '/products',
        padding: 'lg',
        background: 'surface',
      }),
    slideshow: () =>
      slideshowSectionSchema.parse({
        type: 'slideshow',
        blocks: [createBlock('slideshow')],
      }),
    featured_products: () => featuredProductsSectionSchema.parse({ type: 'featured_products' }),
    recently_viewed: () => recentlyViewedSectionSchema.parse({ type: 'recently_viewed' }),
    // The three tabs nearly every shop runs. A tab that has nothing to show -
    // no sales yet, nothing marked down - is hidden on the shop by itself.
    product_tabs: () =>
      productTabsSectionSchema.parse({
        type: 'product_tabs',
        blocks: [
          productTabBlockSchema.parse({ label: 'New arrivals', source: 'newest' }),
          productTabBlockSchema.parse({ label: 'Best sellers', source: 'best_selling' }),
          productTabBlockSchema.parse({ label: 'On sale', source: 'on_sale' }),
        ],
      }),
    categories: () => categoriesSectionSchema.parse({ type: 'categories' }),
    // Settings only: the links are always the shop's own categories.
    category_strip: () =>
      categoryStripSectionSchema.parse({ type: 'category_strip', padding: 'sm' }),
    image_with_text: () =>
      imageWithTextSectionSchema.parse({
        type: 'image_with_text',
        heading: 'Our story',
        body: 'A paragraph about how you started and what you care about.',
      }),
    gallery: () => gallerySectionSchema.parse({ type: 'gallery', blocks: [] }),
    // Three tiles in the default mosaic, as palette-coloured blocks until the
    // merchant adds pictures. The words are prompts, not a claimed discount.
    promo_grid: () =>
      promoGridSectionSchema.parse({
        type: 'promo_grid',
        blocks: [
          promoTileBlockSchema.parse({
            eyebrow: 'Featured',
            heading: 'Your biggest offer',
            subheading: 'One line on why it is worth a look.',
            cta_label: 'Shop now',
            href: '/products',
          }),
          promoTileBlockSchema.parse({ heading: 'New arrivals', cta_label: 'Explore', href: '/products' }),
          promoTileBlockSchema.parse({ heading: 'Best sellers', cta_label: 'Explore', href: '/products' }),
        ],
      }),
    // No brands: they are the shop's own, and with none uploaded the strip
    // shows the brands on its products.
    brand_strip: () =>
      brandStripSectionSchema.parse({ type: 'brand_strip', title: 'Shop by brand', blocks: [] }),
    // No picture and no hotspots: the photo and the products are the shop's own.
    lookbook: () =>
      lookbookSectionSchema.parse({ type: 'lookbook', title: 'Shop the look', blocks: [] }),
    // No pictures: a before and after is only honest with the merchant's own pair.
    before_after: () =>
      beforeAfterSectionSchema.parse({
        type: 'before_after',
        title: 'See the difference',
        before_label: 'Before',
        after_label: 'After',
      }),
    // Three plans with the middle one highlighted, the way most shops sell
    // them. No prices: those are the merchant's to set, so the cards say so.
    pricing_plans: () =>
      pricingPlansSectionSchema.parse({
        type: 'pricing_plans',
        title: 'Choose your plan',
        blocks: [
          pricingPlanBlockSchema.parse({
            name: 'Starter',
            features: 'What the plan includes\nOne point per line',
            cta_label: 'Choose plan',
          }),
          pricingPlanBlockSchema.parse({
            name: 'Popular',
            features: 'What the plan includes\nOne point per line',
            cta_label: 'Choose plan',
            badge: 'Most popular',
            featured: true,
          }),
          pricingPlanBlockSchema.parse({
            name: 'Complete',
            features: 'What the plan includes\nOne point per line',
            cta_label: 'Choose plan',
          }),
        ],
      }),
    // No product: empty shows the shop's newest until the merchant picks one.
    product_spotlight: () =>
      productSpotlightSectionSchema.parse({ type: 'product_spotlight', padding: 'lg' }),
    // A dark band in the theme's own colours - its text colour behind, its
    // page colour for the words - so it follows any palette. No product and
    // no picture: empty shows the shop's newest until the merchant picks one.
    product_showcase: () =>
      productShowcaseSectionSchema.parse({
        type: 'product_showcase',
        padding: 'lg',
        background_color: 'text',
        text_color: 'background',
        heading: 'Made for your routine',
        body: 'One line on what makes this product worth reaching for every day.',
        cta_label: 'Shop now',
        cta_href: '/products',
      }),
    // No video and no heading: a stand-in link would be somebody else's video
    // playing on the merchant's shop, which is a claim rather than a
    // placeholder. The section says what it needs instead - see `Video` in the
    // renderer - which is the same answer `testimonials` gives.
    video: () => videoSectionSchema.parse({ type: 'video' }),
    // Every common network, every URL empty. The rows are the prompt - a
    // merchant fills in the profiles they have and leaves the rest, and the
    // ones they leave are not rendered, so nothing here claims the shop is on
    // a network it is not. See `DEFAULT_SOCIAL_PLATFORMS`.
    social_links: () =>
      socialLinksSectionSchema.parse({
        type: 'social_links',
        title: 'Follow us',
        blocks: DEFAULT_SOCIAL_PLATFORMS.map((platform) =>
          socialLinkBlockSchema.parse({ platform }),
        ),
      }),
    features: () =>
      featuresSectionSchema.parse({
        type: 'features',
        blocks: [
          featureBlockSchema.parse({ icon: 'truck', title: 'Fast delivery' }),
          featureBlockSchema.parse({ icon: 'return', title: 'Easy returns' }),
          featureBlockSchema.parse({ icon: 'payment', title: 'Secure payments' }),
          featureBlockSchema.parse({ icon: 'support', title: 'Here to help' }),
        ],
      }),
    testimonials: () => testimonialsSectionSchema.parse({ type: 'testimonials', blocks: [] }),
    faq: () => faqSectionSchema.parse({ type: 'faq', blocks: [createBlock('faq')] }),
    // Three steps nearly every story has, worded as prompts rather than
    // claims: the year is "Year", so nothing here dates the merchant's shop.
    timeline: () =>
      timelineSectionSchema.parse({
        type: 'timeline',
        blocks: [
          timelineBlockSchema.parse({ date: 'Year', title: 'Where it began', body: 'How and why you started.' }),
          timelineBlockSchema.parse({ date: 'Year', title: 'A step forward', body: 'A milestone you are proud of.' }),
          timelineBlockSchema.parse({ date: 'Today', title: 'Where we are now', body: 'What you offer today.' }),
        ],
      }),
    text: () => textSectionSchema.parse({ type: 'text', title: 'About us', body: '' }),
    // From the shop's categories: nothing to type before it shows something.
    link_directory: () =>
      linkDirectorySectionSchema.parse({ type: 'link_directory', title: 'Popular searches' }),
    banner: () =>
      bannerSectionSchema.parse({ type: 'banner', text: 'Free delivery on orders over ₹999' }),
    contact_form: () =>
      contactFormSectionSchema.parse({ type: 'contact_form', padding: 'lg' }),
    newsletter: () => newsletterSectionSchema.parse({ type: 'newsletter' }),
    // No end time: a date picked for the merchant would be a sale they did not
    // plan. The customiser prompts for one; the live shop shows nothing until then.
    countdown: () => countdownSectionSchema.parse({ type: 'countdown', background: 'surface' }),
    bmi_calculator: () => bmiCalculatorSectionSchema.parse({ type: 'bmi_calculator', padding: 'lg' }),
    // Four promises nearly every shop makes, on a small band: words the
    // merchant overwrites, never a claim about this shop's own products.
    marquee: () =>
      marqueeSectionSchema.parse({
        type: 'marquee',
        padding: 'sm',
        background: 'primary',
        blocks: [
          marqueeItemBlockSchema.parse({ text: 'Free delivery', icon: 'truck' }),
          marqueeItemBlockSchema.parse({ text: 'Easy returns', icon: 'return' }),
          marqueeItemBlockSchema.parse({ text: 'Secure payments', icon: 'payment' }),
          marqueeItemBlockSchema.parse({ text: 'Here to help', icon: 'support' }),
        ],
      }),
    blog_posts: () => blogPostsSectionSchema.parse({ type: 'blog_posts' }),
    language_selector: () => languageSelectorSectionSchema.parse({ type: 'language_selector' }),
    // One service rather than none: an empty calendar is a section a merchant
    // cannot tell from a broken one. The name is a prompt, never a claim about
    // what this particular business does - the same rule the page templates
    // follow.
    bookings: () =>
      bookingsSectionSchema.parse({
        type: 'bookings',
        padding: 'lg',
        blocks: [createBlock('bookings')],
      }),
    // Settings only: the branches are the merchant's own rows, never a sample.
    store_locator: () =>
      storeLocatorSectionSchema.parse({ type: 'store_locator', padding: 'lg' }),
    // Settings only, like the locator: the people are the merchant's rows.
    team: () => teamSectionSchema.parse({ type: 'team', padding: 'lg' }),
    // Settings only, like the team: the roles are the merchant's rows.
    careers: () => careersSectionSchema.parse({ type: 'careers', padding: 'lg' }),
    // No sample reviews: they are what customers write, so the section starts
    // with its settings alone and the reviews arrive from the server.
    product_reviews: () =>
      productReviewsSectionSchema.parse({ type: 'product_reviews', padding: 'lg' }),
    // Anchors start entirely inherited: every setting null or `'theme'`, which
    // is a page that renders exactly as this theme rendered it before the
    // merchant could arrange anything around it.
    product_details: () => productDetailsSectionSchema.parse({ type: 'product_details' }),
    product_list: () => productListSectionSchema.parse({ type: 'product_list' }),
    category_list: () => categoryListSectionSchema.parse({ type: 'category_list' }),
    search_results: () => searchResultsSectionSchema.parse({ type: 'search_results' }),
    cart_lines: () => cartLinesSectionSchema.parse({ type: 'cart_lines' }),
    checkout_form: () => checkoutFormSectionSchema.parse({ type: 'checkout_form' }),
    customer_login: () => customerLoginSectionSchema.parse({ type: 'customer_login' }),
    customer_register: () =>
      customerRegisterSectionSchema.parse({ type: 'customer_register' }),
    customer_account: () =>
      customerAccountSectionSchema.parse({ type: 'customer_account' }),
    blog_index: () => blogIndexSectionSchema.parse({ type: 'blog_index' }),
    blog_post: () => blogPostSectionSchema.parse({ type: 'blog_post' }),
    team_member_details: () =>
      teamMemberDetailsSectionSchema.parse({ type: 'team_member_details' }),
    not_found: () => notFoundSectionSchema.parse({ type: 'not_found' }),
  };

  return drafts[type]();
}

// --- Responsive -----------------------------------------------------------
/**
 * The padding a section stands at on one screen size.
 *
 * Desktop is what the merchant authored; the narrower two inherit it until
 * they are given a value of their own. One function rather than three fields
 * read at three call sites, so "unset means the same as desktop" is stated
 * once and cannot be forgotten by whichever of the renderer, the preview or
 * the inspector was written last.
 */
export function sectionPadding(section: Section, device: SectionDevice): SectionPadding {
  if (device === 'tablet') return section.padding_tablet ?? section.padding;
  if (device === 'mobile') return section.padding_mobile ?? section.padding;
  return section.padding;
}

/** Whether the section is painted at all on one screen size. */
export function sectionShowsOn(section: Section, device: SectionDevice): boolean {
  return section.visible && !section.hidden_on.includes(device);
}

/** True when the merchant has said anything about a narrower screen. */
export function hasResponsiveOverrides(section: Section): boolean {
  return (
    section.hidden_on.length > 0 ||
    section.padding_tablet !== null ||
    section.padding_mobile !== null
  );
}

// --- Catalog sections ----------------------------------------------------
/**
 * Sections whose blocks are copies of a store-level catalog rather than the
 * merchant's own writing: a bookings section's services (`/services` in the
 * panel), a pricing section's plan cards (`/pricing-plans`) and an FAQ
 * section's questions (`/faqs`).
 *
 * The backend writes those blocks from the catalog every time it serves a
 * theme config - the published one and the customizer's draft alike - so
 * anything the customizer typed into them would be overwritten on the next
 * load. The customizer therefore treats them as read-only: no rows in the
 * tree, no "add", no per-block panel. What it does decide is which entries
 * the section shows, as `service_ids` / `plan_ids` / `faq_ids`.
 *
 * Only on a host with a store, mind: a platform owner's catalog theme has no
 * catalogs, so there these blocks are still edited one by one (see
 * `useEditableBlocks` in the customizer).
 */
export const CATALOG_SECTION_TYPES = ['bookings', 'pricing_plans', 'faq'] as const;
export type CatalogSectionType = (typeof CATALOG_SECTION_TYPES)[number];
export type CatalogSection = BookingsSection | PricingPlansSection | FaqSection;

export function isCatalogSection(section: Pick<Section, 'type'>): section is CatalogSection {
  return (CATALOG_SECTION_TYPES as readonly string[]).includes(section.type);
}

/** True for a section with blocks the merchant edits here, one by one. */
export function hasEditableBlocks(section: Section): section is BlockSection {
  return hasBlocks(section) && !isCatalogSection(section);
}

/** The catalog ids a section is narrowed to; empty is "all of them". */
export function catalogPicks(section: CatalogSection): string[] {
  switch (section.type) {
    case 'bookings':
      return section.service_ids;
    case 'pricing_plans':
      return section.plan_ids;
    case 'faq':
      return section.faq_ids;
  }
}

/**
 * A catalog section's blocks narrowed to the picked ids, in the blocks' own
 * order (which is the catalog's). No picks is every block.
 *
 * The server already narrows what it serves, so on a published page this is a
 * no-op. It is here for the customizer: a merchant ticking a service off sees
 * it leave the preview at once, rather than after the next save and reload -
 * and because the draft keeps every hydrated block, ticking it back on brings
 * it back without a round trip.
 */
export function pickedBlocks<S extends CatalogSection>(section: S): S['blocks'] {
  const picks = catalogPicks(section);
  if (picks.length === 0) return section.blocks;
  const wanted = new Set(picks);
  return (section.blocks as Array<S['blocks'][number]>).filter((block) =>
    wanted.has(block.id),
  ) as S['blocks'];
}

/**
 * The blocks a section paints, hidden ones included only where the caller is
 * the customiser - which shows them dimmed rather than dropping them, so the
 * merchant can see what they took down and where it stood.
 */
export function shownBlocks<T extends { visible: boolean }>(
  blocks: readonly T[],
  includeHidden = false,
): T[] {
  return includeHidden ? [...blocks] : blocks.filter((block) => block.visible);
}

// --- Copying --------------------------------------------------------------
/**
 * A copy of one section with an identity of its own, blocks included.
 *
 * Two sections sharing an id share a selection, a React key and - once one of
 * them is dragged - each other's settings. Every path that produces a second
 * copy of a section (duplicate, paste, insert a saved section, insert a
 * theme's preset) goes through here rather than reimplementing the walk.
 */
export function copySection(section: Section): Section {
  return {
    ...section,
    id: newElementId(),
    ...('blocks' in section
      ? { blocks: section.blocks.map((block) => ({ ...block, id: newElementId() })) }
      : {}),
  } as Section;
}

/** The same, for one block. */
export function copyBlock<T extends { id: string }>(block: T): T {
  return { ...block, id: newElementId() };
}

/**
 * How one block is named in a list, before the merchant has written anything.
 *
 * The same rule `sectionTitle` follows one level down: whatever copy the block
 * carries, and its position in the section otherwise. Lives here rather than
 * in the customiser because two lists now name blocks - the section tree and
 * the settings panel's header - and two rules would disagree the first time a
 * block gained a field.
 */
export function blockTitle(block: Block, index: number, fallback: string): string {
  // A social link is titled by its network, not by a field the merchant filled
  // in: an empty row still has to say "Instagram" in the list, because an
  // unfilled row is the normal state of this section rather than a mistake.
  if ('platform' in block) {
    const own = block.label.trim();
    return own || SOCIAL_PLATFORM_LABELS[block.platform];
  }
  // A lookbook hotspot: its own label, else the product it points at.
  if ('product' in block && 'x' in block) {
    const own = (block.label.trim() || block.product.trim()).slice(0, 32);
    return own || `${fallback} ${index + 1}`;
  }
  // A milestone: its date in front of its title, the way the story reads it.
  if ('date' in block) {
    const own = [block.date.trim(), block.title.trim()].filter(Boolean).join(' · ').slice(0, 40);
    return own || `${fallback} ${index + 1}`;
  }

  const named =
    'heading' in block
      ? block.heading
      : 'title' in block
        ? block.title
        : 'name' in block
          ? block.name
          : 'question' in block
            ? block.question
            : 'quote' in block
              ? block.quote
              : 'caption' in block
                ? block.caption
                : 'source' in block
                  ? block.label
                  : '';
  const trimmed = (named ?? '').trim();
  if (!trimmed) return `${fallback} ${index + 1}`;
  return trimmed.length > 32 ? `${trimmed.slice(0, 32)}…` : trimmed;
}
